import { Controller } from "stimulus";
import { Formio } from "formiojs";
import FormioUtils from "formiojs/utils";
import ProbeTool from "../src/Probe";
import IOLProbeTool from "../src/IOLProbeTool";
import PupilProbeTool from "../src/PupilProbeTool";
import CorneaProbeTool from "../src/CorneaProbeTool";
import Hammer from "hammerjs";
import * as cornerstone from "cornerstone-core";
import cornerstoneTools from "cornerstone-tools";
import cornerstoneMath from "cornerstone-math";
cornerstoneTools.ProbeTool = ProbeTool;
import * as cornerstoneWebImageLoader from "cornerstone-web-image-loader";
import utils from "formiojs/utils";
import BaseFileModule from "./shared_modules/base_file_module.js";
import FormStateModule from "./shared_modules/form_state_module.js";
import NotificationModule from "./shared_modules/notification_module.js";
import FormSubmissionModule from "./shared_modules/form_submission_module.js";
import FileProcessingModule from "./shared_modules/file_processing_module.js";
import EllipseGradingModule from "./shared_modules/ellipse_grading_module.js";
import ToggleModule from "./shared_modules/toggle_module.js";
import RFCModule from "./shared_modules/rfc_module.js";
import PrintFormModule from "./shared_modules/print_form_module.js";
import FormAuditHistoryModule, {
  buildAuditHistoryJsonUrl,
} from "./shared_modules/form_audit_history_module.js";
import PreviousVisitValidationModule from "./shared_modules/previous_visit_validation_module.js";
const {
  PanTool,
  WwwcTool,
  ZoomMouseWheelTool,
  LengthTool,
  ArrowAnnotateTool,
  AngleTool,
  EllipticalRoiTool,
  EraserTool,
  RectangleRoiTool,
  FreehandRoiTool,
} = cornerstoneTools;

const LEFT_CLICK = 1;
const MIDDLE_CLICK = 4;
// const RIGHT_CLICK = 2;

export default class extends Controller {
  static targets = [
    "viewer",
    "url",
    "form",
    "path",
    "agreement",
    "users",
    "regradingForm",
    "regraders",
    "menu",
    "menuIcon",
    "formSection",
    "cornerstone",
    "cornerstoneIcon",
    "questionArea",
    "submit",
    "update",
    "loadingImage",
    "button",
    "thumb",
    "auditHistory",
    "changeSummary",
  ];

  async initialize() {
    // Define Targets
    const elem = this.hasViewerTarget ? this.viewerTarget : null;
    this.gradingFormElem = this.formTarget;
    // Optional in view_only (submit panel may be omitted); missing targets previously left the loader spinning
    this.gradingFormAgreementElem = this.hasAgreementTarget
      ? this.agreementTarget
      : null;
    this.annotationUsersElem = this.hasUsersTarget ? this.usersTarget : null;
    this.regradersElems = this.regradersTargets;
    this.regradingFormElem = this.hasRegradingFormTarget
      ? this.regradingFormTarget
      : null;
    this.imagesMenu = this.menuTarget;
    this.formSection = this.formSectionTarget;
    this.cornerstoneViewer = this.cornerstoneTarget;
    this.filename = this.data.get("fileName");
    const imageKeys = this.data.get("imageKeys");
    this.adjImages = imageKeys
      ? imageKeys.split(",").map((key) => key.trim())
      : [];
    this.viewerElement = elem;
    this.gradingSaveUrl =
      document.location.origin + this.data.get("updatePath") + ".json";
    this.gradingPreviousUrl =
      document.location.origin + this.data.get("previousAns") + ".json";
    const previousVisitGradingPath = this.data.get("previousVisitGradingUrl");
    this.previousVisitGradingUrl = previousVisitGradingPath
      ? document.location.origin + previousVisitGradingPath
      : null;
    this.previousVisitValidationModule = null;
    this.graderNameUrl =
      document.location.origin + this.data.get("graderNamePath") + ".json";
    const rfcHistoryPath = this.data.get("adjudicationRfcHistory");
    this.adjudicationRfcHistory = rfcHistoryPath
      ? document.location.origin + rfcHistoryPath + ".json"
      : null;
    this.regradingUrl =
      document.location.origin + this.data.get("regradePath") + ".json";
    this.apiUrl = `${this.data.get("apiUrl")}iol/angle-calculation`;
    this.apiKey = this.data.get("apiKey");
    this.gradingFormUserId = this.data.get("userId");
    this.adjudicatorUserId = this.data.get("adjudicatorUserId"); // Actual adjudicator who submitted
    this.pageType = this.data.get("pageType"); // view_only or regular
    this.gradingFormStackId = this.data.get("stackId");
    this.releaseEye = this.data.get("releaseEye");
    this.circleCount = 0; // Initialize circleCount here
    this.images = [];
    this.readImages();
    this.currentImage = 0;
    this.gradingFormVersionId = this.data.get("versionId");
    this.ellipseData = JSON.parse(this.data.get("ellipseData"));
    this.gradingEllipseData = JSON.parse(this.data.get("gradingEllipseData"));
    // Check if ellipseData is not an empty object

    this.viewerStatus = this.data.get("viewerStatus");
    this.formSaveDraftStatus = false;
    // load previously saved grading data if available
    this.pdfIcon = this.data.get("pdfIcon");
    this.annotation = JSON.parse(this.data.get("annotation"));
    this.donwloadUrl = this.data.get("downloadAnnotationPath");
    this.annotationIsLoadedOnce = {};
    this.graderAnnotations = JSON.parse(this.data.get("graderAnnotations"));
    // Responses from Graders
    this.graderResponses = JSON.parse(this.data.get("graderResponses"));
    // get all files of graders
    this.imagesUrls = {};
    // Iterate through each GradingForm object in your collection
    this.graderResponses.forEach((gradingForm) => {
      // Access the responses.files data from the GradingForm object
      const responsesFiles = gradingForm.responses.files;
      for (const fileKey in responsesFiles) {
        if (responsesFiles.hasOwnProperty(fileKey)) {
          const fileUrls = responsesFiles[fileKey];
          if (this.imagesUrls[fileKey]) {
            // If the file name is already a key in this.imagesUrls, append the new URLs to the existing array
            this.imagesUrls[fileKey] =
              this.imagesUrls[fileKey].concat(fileUrls);
          } else {
            // If the file name is not already a key, create a new key with an array containing the URLs
            this.imagesUrls[fileKey] = fileUrls;
          }
        }
      }
    });
    // end
    this.graderCount = this.data.get("graderRequire");
    this.StudyEye = this.data.get("studyEye");
    this.CheckEye = this.data.get("checkEye");
    this.gradingFormSchema = JSON.parse(this.data.get("gradingForm"));
    if (this.CheckEye == "Study Eye") {
      const updatedComponents = [];
      this.gradingFormSchema.components.forEach((multiple_trials) => {
        if (
          multiple_trials["key"].slice(-2).toLowerCase() == this.StudyEye ||
          this.StudyEye == "ou" ||
          (multiple_trials["key"].slice(-2).toLowerCase() != "od" &&
            multiple_trials["key"].slice(-2).toLowerCase() != "os")
        ) {
          updatedComponents.push(multiple_trials);
        }
      });
      this.gradingFormSchema["components"] = updatedComponents;
    } else {
      const updatedComponents = [];
      this.gradingFormSchema.components.forEach((multiple_trials) => {
        if (
          multiple_trials["key"].slice(-2).toLowerCase() == this.releaseEye ||
          this.releaseEye == "ou" ||
          (multiple_trials["key"].slice(-2).toLowerCase() != "od" &&
            multiple_trials["key"].slice(-2).toLowerCase() != "os")
        ) {
          updatedComponents.push(multiple_trials);
        }
      });
      this.gradingFormSchema["components"] = updatedComponents;
    }
    this.gradingFormResponses = JSON.parse(this.data.get("responses"));
    this.gradingFormSaveUrl =
      document.location.origin + this.data.get("formUpdatePath") + ".json";
    // renderer
    this.gradingForm = await Formio.createForm(
      this.gradingFormElem,
      this.gradingFormSchema,
      {
        noAlerts: true,
        readOnly: this.data.get("readOnly") === "true" || new URLSearchParams(window.location.search).get("page_type") === "view_only",
      },
    );
    // set the draft responses if any
    this.gradingForm.submission = this.gradingFormResponses;
    this.visibilityStates = {
      iol: "both",
      pupil: "both",
      cornea: "both",
    };
    this.activeTypes = {
      iol: true,
      pupil: true,
      cornea: true,
    };

    //  Corner Stone Setup
    if (this.viewerStatus == "true") {
      cornerstoneWebImageLoader.external.cornerstone = cornerstone;
      cornerstoneTools.external.cornerstone = cornerstone;
      cornerstoneTools.external.Hammer = Hammer;
      cornerstoneTools.external.cornerstoneMath = cornerstoneMath;
      cornerstoneTools.init({
        mouseEnabled: true,
        touchEnabled: true,
        globalToolSyncEnabled: true,
        showSVGCursors: true,
      });
      cornerstoneTools.loadHandlerManager.setErrorLoadingHandler(
        (element, imageId, error) => {
          console.error(imageId);
          // throw error;
        },
      );
      const imageUrl = document.location.origin + "/" + this.url;
      cornerstone.enable(elem);
      // cornerstone.registerImageLoader = 'dicomweb';
      cornerstone.loadAndCacheImage(imageUrl).then((image) => {
        this.loadExistingAnnotationsForImage(imageUrl);
        cornerstone.displayImage(elem, image);
        // Activate Pan Tool on Middle Click Drag
        cornerstoneTools.addTool(PanTool);
        cornerstoneTools.setToolActive("Pan", {
          mouseButtonMask: MIDDLE_CLICK,
        });
        // Activate WWWc tool on Left Click Drag
        cornerstoneTools.addTool(WwwcTool);
        cornerstoneTools.setToolActive("Wwwc", { mouseButtonMask: LEFT_CLICK });
        // Activate Zoom Mouse Wheel Tool
        cornerstoneTools.addTool(ZoomMouseWheelTool, {
          // Optional configuration
          configuration: {
            invert: false,
            preventZoomOutsideImage: false,
            minScale: 0.01,
            maxScale: 20.0,
          },
        });
        cornerstoneTools.setToolActive("ZoomMouseWheel", {});
        // Add Non Active tools
        cornerstoneTools.addTool(LengthTool);
        cornerstoneTools.addTool(IOLProbeTool);
        cornerstoneTools.addTool(PupilProbeTool);
        cornerstoneTools.addTool(CorneaProbeTool);
        cornerstoneTools.addTool(ArrowAnnotateTool);
        cornerstoneTools.addTool(AngleTool);
        cornerstoneTools.addTool(RectangleRoiTool);
        cornerstoneTools.addTool(FreehandRoiTool);
        cornerstoneTools.addTool(EraserTool);
        cornerstoneTools.addTool(cornerstoneTools.ProbeTool);
        cornerstoneTools.addTool(EllipticalRoiTool, {
          configuration: {
            drawHandlesOnHover: false,
            hideHandlesIfMoving: true,
            renderDashed: false,
          },
        });
        // register cornerstone tools events
        // https://github.com/cornerstonejs/cornerstoneTools/blob/master/src/events.js
        elem.addEventListener("cornerstonetoolsmeasurementcompleted", (e) => {
          // console.table(e.detail.measurementData);
        });
        this.refreshTools();
      });

      this.viewerElement.addEventListener(
        "cornerstonetoolsmeasurementadded",
        (e) => {
          // On new measurement added
          if (e.detail.toolName !== "Probe") {
            return;
          }
          this.circleCount++;
          this.appendCountInTheForm(this.circleCount);
        },
      );

      this.viewerElement.addEventListener(
        "cornerstonetoolsmeasurementremoved",
        (e) => {
          // On measurement removed
          if (e.detail.toolName !== "Probe") {
            return;
          }
          this.circleCount--;
          this.appendCountInTheForm(this.circleCount);
        },
      );

      this.viewerElement.addEventListener("recalculateCircleCount", (e) => {
        const circleCount = e.detail.circleCount;
        // Update form with new circle count
        this.appendCountInTheForm(circleCount);
      });
      // attach event listeners
      this.viewerElement.addEventListener("cornerstonenewimage", (e) => {
        e.detail.viewport.displayedArea.brhc.x = e.detail.image.width;
        e.detail.viewport.displayedArea.brhc.y = e.detail.image.height;
      });
    }
    // end
    // Initialize shared modules
    this.baseFile = new BaseFileModule(this.element);
    this.formState = new FormStateModule(
      this.gradingForm,
      this.formTarget,
      this.hasSubmitTarget ? this.submitTarget : null,
      this.hasUpdateTarget ? this.updateTarget : null,
    );
    this.notifications = new NotificationModule(
      this.hasLoadingImageTarget ? this.loadingImageTarget : null,
    );
    this.fileProcessingModule = new FileProcessingModule({
      notifications: this.notifications,
      visitDate: this.data.get("visitDate"),
      subjectCode: this.data.get("subjectCode"),
      studyName: this.data.get("studyName"),
      sdarUrl: this.data.get("sdarApi"),
      getPreviousVisitValidationModule: () =>
        this.previousVisitValidationModule,
    });
    this.formSubmissionModule = new FormSubmissionModule({
      formSaveUrl:
        document.location.origin + this.data.get("formUpdatePath") + ".json",
      formVersionId: this.data.get("versionId"),
      formUserId: this.data.get("userId"),
      formStackId: this.data.has("stackId") ? this.data.get("stackId") : null,
      notifications: this.notifications,
      stackIsFinalUrl: "",
      images: this.adjImages,
    });
    this.ellipseGrading = new EllipseGradingModule({
      controller: this,
      iolGradingApi: this.data.get("iolGradingService"),
      saveUrl:
        document.location.origin + this.data.get("formUpdatePath") + ".json",
      method: "",
      minPoints: this.data.get("minPoints"),
      iolDiameter: this.data.get("iolDiameter"),
      ellipseData: JSON.parse(this.data.get("ellipseData")),
      cornerstone: cornerstone, // Pass cornerstone dependency
      visibilityStates: this.visibilityStates,
      activeTypes: this.activeTypes,
      gradingEllipseData: this.gradingEllipseData,
      users: this.usersTarget,
    });
    this.RFCModule = new RFCModule({
      qcForm: this.gradingForm, // renamed here
      controller: this,
      status: this.data.get("status"),
    });
    this.RFCModule.handleStatusChange(
      this.gradingForm,
      this.data.get("responses"),
    );

    const auditHistoryPath = this.data.get("auditHistoryUrl");
    if (this.hasAuditHistoryTarget) {
      this.formAuditHistoryModule = new FormAuditHistoryModule({
        form: this.gradingForm,
        formSchema: this.gradingFormSchema,
        auditHistoryUrl: buildAuditHistoryJsonUrl(auditHistoryPath),
        auditHistoryTarget: this.auditHistoryTarget,
        changeSummaryTarget: this.hasChangeSummaryTarget
          ? this.changeSummaryTarget
          : null,
        stackStatus: this.data.get("status"),
      });

      this.formAuditHistoryModule.load();
      this.gradingForm.on("render", () => {
        this.formAuditHistoryModule?.attachQuestionHistoryIcons();
      });
    }

    this.toggleModule = new ToggleModule({
      button: this.buttonTarget,
      thumb: this.thumbTarget,
      toggleSelector: ".additional-tools-adj",
      disableTools: document.getElementsByClassName("disableToolsAdj"),
      ellipseData: this.ellipseData,
      gradingEllipseData: this.gradingEllipseData,
      users: this.usersTarget,
    });
    this.printFormModule = new PrintFormModule({
      controller: this,
      formSchema: this.gradingFormSchema,
      formResponses: this.gradingFormResponses,
      stackId: this.gradingFormStackId,
      studyId: this.data.get("studyId"),
      dataSourceKey: "grading_details",
      endpointPath: "adjudications/get_adjudication_form_details",
      formTitle: "GRADING FORM",
      showModalityTitle: false,
      printFormWithData: (() => {
        const value = this.data.get("printFormWithData");
        return (
          value === "true" || value === true || value === "1" || value === 1
        );
      })(),
      includeAuditSummaryInPdf: (() => {
        const value = this.data.get("includeAuditSummaryInPdf");
        return (
          value === "true" || value === true || value === "1" || value === 1
        );
      })(),
      includeChangeSummaryInPdf: (() => {
        const value = this.data.get("includeChangeSummaryInPdf");
        return (
          value === "true" || value === true || value === "1" || value === 1
        );
      })(),
      // All callbacks are optional now - module provides defaults
      // Using defaults for: renderField, isReasonForChange, shouldHideReasonImagesUngradable,
      // shouldSkipReasonImagesUngradable, extractAnswerValue
      auditHistoryUrl: buildAuditHistoryJsonUrl(auditHistoryPath),
      stackStatus: this.data.get("status"),
    });
    // Set the form in modules
    this.baseFile.setForm(this.gradingForm);
    this.ellipseGrading.addEllipseRenderer();
    this.notifications.hideLoader();
    await this.renderGradingDataAndImages();
    await this.checkifAllImagesUploaded();
    // Add grader initials to thumbnails
    await this.addGraderInitialsToThumbnails();
    // Form state — view_only / missing agreement → always read-only UI
    const processButton = document.getElementById("processBtn");
    const saveBtn = document.getElementById("saveBtn");
    const isViewOnly =
      this.pageType === "view_only" ||
      new URLSearchParams(window.location.search).get("page_type") ===
        "view_only";
    if (isViewOnly || this.gradingFormAgreementElem?.checked) {
      this.formState.disableForm();
      if (processButton) {
        processButton.classList.add("hidden");
      }
      if (saveBtn) {
        saveBtn.classList.add("hidden");
      }
    } else {
      this.formState.enableForm();
      if (processButton) {
        processButton.classList.remove("hidden");
      }
      if (saveBtn) {
        saveBtn.classList.remove("hidden");
      }
    }
    window.addEventListener("successFileUpload", (event) => {
      const dataType = event.detail?.dataType;
      if (dataType !== "SDV") {
        return; // Ignore if data-type is not SDV
      }
      this.fileProcessingModule.handleGlobalFileUpload(
        this.gradingFormElem,
        event,
      );
    });
  }

  connect() {
    // Set up the keyboard shortcut listener when the controller connects
    this.boundKeydownHandler = this.handleKeydown.bind(this);
    window.addEventListener("keydown", this.boundKeydownHandler);
  }

  disconnect() {
    // Clean up the event listener when the controller disconnects
    window.removeEventListener("keydown", this.boundKeydownHandler);
  }

  handleKeydown(event) {
    if (!event.shiftKey) return;

    const viewer = this.viewerTarget;
    if (!viewer) return;

    // Try to get the actual enabled element (usually canvas parent)
    const element = viewer.querySelector("canvas")?.parentElement;
    if (!element || !cornerstone.getEnabledElement) return;

    let enabledElement;
    try {
      enabledElement = cornerstone.getEnabledElement(element);
    } catch (err) {
      console.warn("Cornerstone element is not enabled yet:", err);
      return;
    }

    let dx = 0,
      dy = 0;
    const panAmount = 10; // pixels to pan

    switch (event.key) {
      case "ArrowUp":
        dy = -panAmount;
        break;
      case "ArrowDown":
        dy = panAmount;
        break;
      case "ArrowLeft":
        dx = -panAmount;
        break;
      case "ArrowRight":
        dx = panAmount;
        break;
      default:
        return;
    }

    const viewport = cornerstone.getViewport(element);
    viewport.translation.x += dx;
    viewport.translation.y += dy;
    cornerstone.setViewport(element, viewport);
    event.preventDefault(); // Prevent default scroll behavior
  }

  checkifAllImagesUploaded() {
    this.gradingForm.on("change", () => {
      const qcImagesElements = document.querySelectorAll(".qc_images");
      this.dataQuestionArray = Array.from(qcImagesElements).map((element) =>
        element.getAttribute("data-question"),
      );
    });
  }

  renderGradingDataAndImages() {
    Formio.createForm(
      this.gradingFormElem,
      "https://examples.form.io/example",
    ).then((form) => {
      this.previousVisitValidationModule = new PreviousVisitValidationModule({
        gradingForm: this.gradingForm,
        gradingFormSchema: this.gradingFormSchema,
        getLabelFromSchema: (schema, key, val) =>
          this.getLabelFromSchema(schema, key, val),
        previousVisitGradingUrl: this.previousVisitGradingUrl,
        initialOverrides:
          (this.gradingFormResponses &&
            this.gradingFormResponses.previous_visit_overrides) ||
          {},
      });
      // Attach the callback function to the "render" event
      this.gradingForm.on("render", () => {
        // Call the defined functions
        this.displayGradingAnswerToAdjudication();
        if (Object.keys(this.gradingFormResponses).length > 0) {
          this.displayImagesForQuestions(
            this.gradingForm.components,
            this.gradingFormResponses,
            this.data.get("attachments"),
          );
          this.displayImagesForQuestions(
            this.gradingForm.components,
            this.gradingFormResponses,
            this.data.get("gradingAttachments"),
          );
        }
        this.highlightQuestionsToAdjudicator(
          this.gradingForm.components,
          this.gradingFormResponses,
        );
        if (this.previousVisitValidationModule)
          this.previousVisitValidationModule.attachPreviousVisitConsistencyOnBlur();
      });

      // Attach thebfunction to the "change" event
      this.gradingForm.on("change", () => {
        // defined functions
        this.displayGradingAnswerToAdjudication();
        if (Object.keys(this.gradingFormResponses).length > 0) {
          this.displayImagesForQuestions(
            this.gradingForm.components,
            this.gradingFormResponses,
            this.data.get("attachments"),
          );
          this.displayImagesForQuestions(
            this.gradingForm.components,
            this.gradingFormResponses,
            this.data.get("gradingAttachments"),
          );
        }
        this.highlightQuestionsToAdjudicator(
          this.gradingForm.components,
          this.gradingFormResponses,
        );
      });

      // uncomment the line below to rerender
      this.gradingForm.redraw();

      if (this.gradingForm.disabled) {
        const dropzoneElements = document.getElementsByClassName("dropzone");
        for (const element of dropzoneElements) {
          element.classList.add("hidden");
        }
      }
      this.gradingForm.on("change", (changed) => {
        this.handleReasonForChange(changed);
        if (this.previousVisitValidationModule) {
          this.previousVisitValidationModule.handleFormChange(changed);
        }
      });
      if (this.previousVisitValidationModule) {
        this.previousVisitValidationModule
          .fetchPreviousVisitGradingData()
          .then(() => {
            // Rebuild grader flags once reference user/role metadata is loaded.
            document
              .querySelectorAll(".grader-answers-div")
              .forEach((el) => el.remove());
            this.displayGradingAnswerToAdjudication();
          });
      }
    });
  }

  async displayImagesForQuestions(
    formComponents,
    gradingFormResponses,
    ImagesArray,
  ) {
    // Split the string into an array of URLs using a comma as the separator
    const imagesArray = ImagesArray.split("<==>").map((url) => url.trim());
    // Iterate through the form components
    FormioUtils.eachComponent(formComponents, (component) => {
      // Check if the component key is in gradingFormResponses
      if (component.key in gradingFormResponses.data) {
        const questionKey = component.key;
        const verified_key = questionKey + "_sdv_verified";
        const comments_key = questionKey + "_sdv_comments";
        if (gradingFormResponses.sdv) {
          if (
            gradingFormResponses &&
            gradingFormResponses.sdv &&
            verified_key in gradingFormResponses.sdv
          ) {
            const check_sdv = gradingFormResponses.sdv[verified_key];
            const sdv_comments = gradingFormResponses.sdv[comments_key];
            this.appendSdvWithQuestionTitles(
              component,
              check_sdv,
              sdv_comments,
            );
          }
        }
        if (
          gradingFormResponses &&
          gradingFormResponses.files &&
          questionKey in gradingFormResponses.files
        ) {
          const imageUrls = gradingFormResponses.files[questionKey];
          this.appendAdjudicationImageWithQuestion(
            component,
            imageUrls,
            imagesArray,
          );
        }
      }
      for (const response of this.graderResponses) {
        if (
          "data" in response.responses &&
          component.key in response.responses.data
        ) {
          const questionKey = component.key;
          if (
            this.graderResponses &&
            this.imagesUrls &&
            questionKey in this.imagesUrls
          ) {
            this.appendGradingImageWithQuestion(
              component,
              questionKey,
              this.imagesUrls,
              imagesArray,
            );
          }
        }
      }
    });
  }

  handleReasonForChange(form) {
    if (this.data.get("status") === "editing") {
      console.log(this.data.get("status"), "handleReasonForChange");
      this.RFCModule.handleReasonForChange(form);
    }
  }

  highlightQuestionsToAdjudicator() {
    if (!this.gradingFormAgreementElem?.checked) {
      FormioUtils.eachComponent(this.gradingForm.components, (c) => {
        if (c && c.component && c.element) {
          const element = c.element;
          if (!element.classList.contains("formio-hidden")) {
            const questionKey = c.component.key;
            const responseValues = this.graderResponses.map(
              (r) => r.responses.data[questionKey],
            );
            if (
              responseValues.length > 1 &&
              !responseValues.every((value) => value === responseValues[0])
            ) {
              // Values are not equal, add red border to the parent div
              const parentDiv = element.closest(".formio-component");
              if (parentDiv) {
                parentDiv.classList.add(
                  "border-dashed",
                  "border-2",
                  "border-red-600",
                  "p-1",
                );
              }
            }
          }
        }
      });
    }
  }

  displayGradingAnswerToAdjudication() {
    if (!this.gradingFormAgreementElem?.checked) {
      FormioUtils.eachComponent(this.gradingForm.components, (c) => {
        if (c && c.component && c.element) {
          const element = c.element;
          if (!element.classList.contains("formio-hidden")) {
            let graderAnswersDiv = element.querySelector(".grader-answers-div");

            if (!graderAnswersDiv) {
              graderAnswersDiv = document.createElement("div");
              graderAnswersDiv.classList.add("grader-answers-div");
              element.append(graderAnswersDiv);
            } else {
              return;
            }

            const questionKey = c.component.key;

            let answers = this.graderResponses
              .map((r) => r.responses.data[questionKey])
              .filter((val) => val !== undefined && val !== ""); // Remove undefined values

            let valToCompare = "rand1234";
            let match = true;
            // Append each grader's response
            this.graderResponses.forEach((r, i) => {
              const br = document.createElement("br");
              const span = document.createElement("span");
              span.classList.add("grader-response", "pl-1", "cursor-pointer");
              span.dataset.userId = r["user_id"];
              span.dataset.questionKey = questionKey;
              span.dataset.answer = r.responses.data[questionKey] || "nil"; // Store answer

              if (r.responses.data.hasOwnProperty(questionKey)) {
                const label = this.getLabelFromSchema(
                  this.gradingFormSchema,
                  questionKey,
                  r.responses.data[questionKey],
                );
                const displayLabel = label ? label : "nil";
                const override =
                  r.responses.previous_visit_overrides &&
                  r.responses.previous_visit_overrides[questionKey];
                const enrichedOverride = this.previousVisitValidationModule
                  ? this.previousVisitValidationModule.enrichOverrideWithReferenceUser(
                      override,
                      questionKey,
                    )
                  : override;
                const note = PreviousVisitValidationModule.formatOverrideNote(
                  enrichedOverride,
                  {
                    schema: this.gradingFormSchema,
                    questionKey: questionKey,
                    getLabelFromSchema: (schema, key, val) =>
                      this.getLabelFromSchema(schema, key, val),
                  },
                );
                const overrideNote = note
                  ? ` <span class="${note.className} font-semibold ml-1 cursor-help" title="${String(note.title || "").replace(/"/g, "&quot;")}">${note.icon}</span>`
                  : "";

                this.getGraderName(r["user_id"])
                  .then((userName) => {
                    span.innerHTML = `<a href="#" class="grader-answer-link" data-userid = "${r["user_id"]}" data-question ="${questionKey}">${userName}:: ${displayLabel}</a>${overrideNote}`;
                  })
                  .catch(console.error);
              } else {
                span.innerHTML = `<a href="#" class="grader-answer-link">Grader ${
                  i + 1
                }: Nil</a>`;
                match = false;
              }

              // Add click event listener
              span.addEventListener("click", (event) => {
                event.preventDefault();
                this.appendValueToInput(event);
              });

              graderAnswersDiv.append(span);
              graderAnswersDiv.append(br);
            });

            // If there are two graders, add an "Average" option
            if (c.component.type === "number" && answers.length === 2) {
              let avgValue;
              let isCircularAverage = false;

              // Use circular average for LENS_DECN_DEGREE question
              if (questionKey === "LENS_DECN_DEGREE") {
                isCircularAverage = true;
                avgValue = this.getCircularAverage(
                  Number(answers[0]),
                  Number(answers[1]),
                );
              } else {
                // Use simple average for other number fields
                avgValue = (Number(answers[0]) + Number(answers[1])) / 2;
              }

              const avgSpan = document.createElement("span");
              avgSpan.classList.add("grader-response", "pl-1");

              // Display average value or message if null
              if (avgValue !== null && avgValue !== undefined) {
                avgSpan.classList.add("cursor-pointer");
                avgSpan.dataset.questionKey = questionKey;
                avgSpan.dataset.answer = avgValue.toFixed(3); // Rounded to 3 decimals

                // Use different label for circular average
                const avgLabel = isCircularAverage
                  ? "Circular Average"
                  : "Average";
                avgSpan.innerHTML = `<a href="#" class="grader-answer-link">${avgLabel}: ${avgSpan.dataset.answer}</a>`;

                avgSpan.addEventListener("click", (event) => {
                  event.preventDefault();
                  this.appendValueToInput(event);
                });
              } else if (isCircularAverage) {
                // Show message when circular average can't be calculated
                avgSpan.innerHTML = `<span class="text-warning">Circular Average: Values too far apart (>40°)</span>`;
              }

              graderAnswersDiv.append(avgSpan);
            }

            // Add styling based on match status
            graderAnswersDiv.classList.add(
              "mt-1",
              "border",
              "rounded",
              match ? "border-green-400" : "border-red-400",
            );

            c.element.append(graderAnswersDiv);
          }
        }
      });
    }
  }

  appendValueToInput(event) {
    const span = event.currentTarget;
    const questionKey = span.dataset.questionKey;
    const answer = span.dataset.answer;

    if (!questionKey || !answer) return;

    FormioUtils.eachComponent(this.gradingForm.components, (c) => {
      if (c.component && c.component.key === questionKey) {
        const input =
          (c.refs && c.refs.input && c.refs.input[0]) ||
          (c.element && c.element.querySelector("input, textarea"));
        if (this.previousVisitValidationModule && input) {
          this.previousVisitValidationModule.notifyAppendedFieldValue(
            input,
            answer,
          );
        } else if (c.setValue) {
          c.setValue(answer);
        } else if (input) {
          input.value = answer;
          input.dispatchEvent(new Event("input", { bubbles: true }));
        }
      }
    });
  }

  getCircularAverage(a, b) {
    const diff = Math.abs(a - b); // 7-531 = 344
    const wrapDiff = 360 - diff;
    const THRESHOLD = 40; // use 40 if your manager confirmed 40°

    // Case 1: direct difference small
    if (diff <= THRESHOLD) {
      return (a + b) / 2.0;
    }

    // Case 2: wrap-around difference small
    if (wrapDiff <= THRESHOLD) {
      return ((a + b + 360) / 2.0) % 360;
    }

    // No valid average
    return null;
  }

  getGraderName(userId) {
    return fetch(this.graderNameUrl, {
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-CSRF-Token": document.head
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      method: "POST",
      body: JSON.stringify({
        user: {
          user_id: userId,
        },
      }),
    })
      .then((response) => {
        if (response.ok) {
          return response.json().then((data) => data.full_name);
        } else {
          throw new Error("Failed to fetch user data");
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }

  // Get initials from full name
  getUserInitials(fullName) {
    if (!fullName) return "";
    const nameParts = fullName.trim().split(/\s+/);
    if (nameParts.length === 1) {
      return nameParts[0].substring(0, 2).toUpperCase();
    }
    return nameParts.map((part) => part[0].toUpperCase()).join("");
  }

  // Add grader initials to image thumbnails
  async addGraderInitialsToThumbnails() {
    try {
      const imageBoxes = document.querySelectorAll(".image-box");

      // Determine which user_id to use for adjudicator
      // In view-only mode, use the actual adjudicator's user_id, not the logged-in PM's id
      const isViewOnly = this.pageType === "view_only";
      const adjudicatorUserId =
        isViewOnly && this.adjudicatorUserId
          ? this.adjudicatorUserId
          : this.gradingFormUserId;

      // Get adjudication status from data attribute
      const adjudicationStatus = this.data.get("status");
      const isAdjudicationCompleted =
        adjudicationStatus === "completed" ||
        this.gradingFormAgreementElem?.checked;

      // First, find the final (last processed) image for each user who has submitted
      const finalImages = {}; // { userId: finalImageUrl }

      // Check each grader's final image
      for (const grading of this.graderResponses) {
        if (grading.status === "completed") {
          const userId = grading.user_id;
          const graderAnnotations = this.graderAnnotations?.[userId];

          if (graderAnnotations) {
            const annotatedImageUrls = Object.keys(graderAnnotations);
            if (annotatedImageUrls.length > 0) {
              // Find the final image for this grader
              const finalImageUrl = this.findFinalAnnotatedImage(
                graderAnnotations,
                annotatedImageUrls,
              );
              finalImages[userId] = finalImageUrl;
            }
          }
        }
      }

      // Check adjudicator's final image if submitted
      if (isAdjudicationCompleted && this.annotation) {
        const annotatedImageUrls = Object.keys(this.annotation);
        if (annotatedImageUrls.length > 0) {
          const finalImageUrl = this.findFinalAnnotatedImage(
            this.annotation,
            annotatedImageUrls,
          );
          finalImages[adjudicatorUserId] = finalImageUrl;
        }
      }

      // Process each thumbnail
      for (const box of imageBoxes) {
        const img = box.querySelector(".image");
        if (!img) continue;

        const imageUrl = document.location.origin + "/" + img.dataset.url;

        // Extract filename from URL for matching (ActiveStorage URLs have changing tokens)
        const getFilename = (url) => {
          const parts = url.split("/");
          return parts[parts.length - 1]; // Get last part (filename)
        };

        const currentFilename = getFilename(imageUrl);

        const processedBy = []; // Users who have annotations (processed/drafted)
        const gradedBy = []; // Graders who submitted and this is their final image
        const adjudicatedBy = []; // Adjudicators who submitted and this is their final image

        // Helper function to check if annotations exist for this image (match by filename)
        const hasAnnotationForImage = (annotations) => {
          if (!annotations) return false;
          if (annotations[imageUrl]) return true;
          // Check by filename since ActiveStorage tokens change
          for (const annotationUrl in annotations) {
            const annotationFilename = getFilename(annotationUrl);
            if (annotationFilename === currentFilename) {
              return true;
            }
          }
          return false;
        };

        // Check each grader to see if they have annotations on this image
        for (const grading of this.graderResponses) {
          const userId = grading.user_id;
          const graderAnnotations = this.graderAnnotations?.[userId];

          // If this grader has annotations on this specific image
          if (hasAnnotationForImage(graderAnnotations)) {
            const fullName = await this.getGraderName(userId);
            const initials = this.getUserInitials(fullName);

            // Check if this is the final image for a submitted grading (match by filename)
            const finalImageFilename = finalImages[userId]
              ? getFilename(finalImages[userId])
              : null;
            if (
              grading.status === "completed" &&
              finalImageFilename === currentFilename
            ) {
              gradedBy.push(initials);
            } else {
              processedBy.push(initials);
            }
          }
        }

        // Check adjudicator (use the correct user_id based on mode)
        if (hasAnnotationForImage(this.annotation)) {
          const adjFullName = await this.getGraderName(adjudicatorUserId);
          const adjInitials = this.getUserInitials(adjFullName);

          // Check if this is the final image for submitted adjudication (match by filename)
          const finalImageFilename = finalImages[adjudicatorUserId]
            ? getFilename(finalImages[adjudicatorUserId])
            : null;
          if (
            isAdjudicationCompleted &&
            finalImageFilename === currentFilename
          ) {
            adjudicatedBy.push(adjInitials);
          } else {
            processedBy.push(adjInitials);
          }
        }

        // Remove existing labels if any
        const existingText = box.querySelector(".processed-by-text");
        if (existingText) {
          existingText.remove();
        }

        // Build the label(s) based on the rules
        const labelContainer = document.createElement("div");
        labelContainer.className = "processed-by-text";
        labelContainer.style.fontSize = "8px";
        labelContainer.style.color = "#6B7280";
        labelContainer.style.marginTop = "3px";
        labelContainer.style.textAlign = "center";
        labelContainer.style.lineHeight = "1.3";

        const labels = [];

        if (processedBy.length > 0) {
          labels.push(`Processed by: ${processedBy.join(", ")}`);
        }

        if (gradedBy.length > 0) {
          const link = document.createElement("a");
          link.href = "#";
          link.textContent = `Graded by: ${gradedBy.join(", ")}`;
          link.style.color = "#2563EB";
          link.style.textDecoration = "underline";
          link.style.cursor = "pointer";

          // Add click handler to download image
          link.addEventListener("click", async (e) => {
            e.preventDefault();
            try {
              const response = await fetch(imageUrl);
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const downloadLink = document.createElement("a");
              downloadLink.href = url;

              // Extract filename from URL
              const filename =
                imageUrl.split("/").pop().split("?")[0] || "image.png";
              downloadLink.download = filename;

              document.body.appendChild(downloadLink);
              downloadLink.click();
              document.body.removeChild(downloadLink);
              window.URL.revokeObjectURL(url);
            } catch (error) {
              console.error("Error downloading image:", error);
              // Fallback: open in new tab
              window.open(imageUrl, "_blank");
            }
          });

          const linkDiv = document.createElement("div");
          linkDiv.appendChild(link);
          labelContainer.appendChild(linkDiv);
        }

        if (adjudicatedBy.length > 0) {
          const link = document.createElement("a");
          link.href = "#";
          link.textContent = `Adjudicated by: ${adjudicatedBy.join(", ")}`;
          link.style.color = "#2563EB";
          link.style.textDecoration = "underline";
          link.style.cursor = "pointer";

          // Add click handler to download image
          link.addEventListener("click", async (e) => {
            e.preventDefault();
            try {
              const response = await fetch(imageUrl);
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const downloadLink = document.createElement("a");
              downloadLink.href = url;

              // Extract filename from URL
              const filename =
                imageUrl.split("/").pop().split("?")[0] || "image.png";
              downloadLink.download = filename;

              document.body.appendChild(downloadLink);
              downloadLink.click();
              document.body.removeChild(downloadLink);
              window.URL.revokeObjectURL(url);
            } catch (error) {
              console.error("Error downloading image:", error);
              // Fallback: open in new tab
              window.open(imageUrl, "_blank");
            }
          });

          const linkDiv = document.createElement("div");
          linkDiv.appendChild(link);
          labelContainer.appendChild(linkDiv);
        }

        // Add non-link labels if any
        if (labels.length > 0) {
          const textDiv = document.createElement("div");
          textDiv.innerHTML = labels.join("<br>");
          labelContainer.insertBefore(textDiv, labelContainer.firstChild);
        }

        // Add label container if it has content
        if (labelContainer.children.length > 0 || labels.length > 0) {
          box.appendChild(labelContainer);
        }
      }
    } catch (error) {
      console.error("Error adding grader initials to thumbnails:", error);
    }
  }

  appendAdjudicationImageWithQuestion(component, imageUrls, imagesArray) {
    if (
      component.element &&
      component.element.querySelector(".adjudication-screenshots") &&
      component.element.querySelector(".adjudication-files")
    ) {
      return; // If already appended, exit the function
    }

    const addedFiles = new Set();

    const adjudicationSpan = document.createElement("p");
    adjudicationSpan.classList.add("adjudication-screenshots");
    adjudicationSpan.textContent = "Adjudication Screenshots and Files";

    const container = document.createElement("div");
    container.classList.add(
      "mt-1",
      "border",
      "rounded",
      "border-green-300",
      "flex",
      "gap-2",
      "adjudication-files",
    );

    for (const imageUrl of imageUrls) {
      for (let i = 0; i < imagesArray.length; i++) {
        const [blobId, fullUrl] = imagesArray[i].split("<!==!>");
        const decodedImageUrl = decodeURIComponent(fullUrl);
        const imageUrlParts = decodedImageUrl.split("/");
        const imageName = imageUrlParts[imageUrlParts.length - 1];

        if (imageUrl === imageName && !addedFiles.has(imageName)) {
          const fileExtension = imageName.split(".").pop().toLowerCase();

          if (fileExtension === "pdf") {
            const pdfIconElement = document.createElement("img");
            pdfIconElement.setAttribute("src", this.pdfIcon);
            pdfIconElement.setAttribute("alt", "Attachment Image");
            pdfIconElement.setAttribute("width", "100");
            pdfIconElement.setAttribute("height", "100");
            pdfIconElement.style.cursor = "pointer";

            const pdfLink = document.createElement("a");
            pdfLink.setAttribute("href", decodedImageUrl);
            pdfLink.setAttribute("target", "_blank");
            pdfLink.appendChild(pdfIconElement);

            const fileContainer = document.createElement("div");
            fileContainer.classList.add("file-container");
            fileContainer.appendChild(pdfLink);
            container.appendChild(fileContainer);
          } else {
            const imageElement = document.createElement("img");
            imageElement.setAttribute("src", decodedImageUrl);
            imageElement.setAttribute("alt", "Attachment Image");
            imageElement.setAttribute("width", "100");
            imageElement.setAttribute("height", "100");
            imageElement.style.cursor = "pointer";
            imageElement.setAttribute("data-blob-id", blobId);

            const deleteIcon = this._createDeleteIcon(
              imageName,
              component.key,
              decodedImageUrl,
              blobId,
            );

            const imageContainer = document.createElement("div");
            imageContainer.classList.add("image-container");
            imageContainer.appendChild(imageElement);
            imageContainer.appendChild(deleteIcon);

            imageElement.addEventListener("click", () => {
              const lightboxImage = document.getElementById("lightbox-image");
              lightboxImage.src = decodedImageUrl;
              const lightboxContainer =
                document.getElementById("lightbox-container");
              lightboxContainer.style.display = "block";
            });

            const lightboxContainer =
              document.getElementById("lightbox-container");
            lightboxContainer.addEventListener("click", () => {
              lightboxContainer.style.display = "none";
            });

            container.appendChild(imageContainer);
          }

          addedFiles.add(imageName);
        }
      }
    }

    if (
      container.innerHTML.trim() !== "" &&
      adjudicationSpan.innerHTML.trim() !== ""
    ) {
      if (component.element) {
        component.element.appendChild(container);
        component.element.appendChild(adjudicationSpan);
      }
    }
  }

  _createDeleteIcon(imageName, componentKey, imageUrl, blobId) {
    const deleteIcon = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg",
    );
    const attributes = {
      xmlns: "http://www.w3.org/2000/svg",
      width: "20",
      height: "20",
      viewBox: "0 0 24 24",
      "data-filename": imageName,
      "data-key": componentKey,
      "data-url": imageUrl,
      "data-id": blobId,
      "data-action": "click->adjudications#_deleteImage",
    };

    Object.keys(attributes).forEach((key) => {
      deleteIcon.setAttribute(key, attributes[key]);
    });

    if (!this.gradingForm._disabled) {
      deleteIcon.innerHTML = `
                <title>Delete Attachment</title>
                <path fill="#FF0000" d="M18 4h-4.18l-1.82-2H10L8.82 4H4v2h16V4zM6.36 8h11.28l1.23 10.96l.01.04V20H4v-1.96l.01-.04L5.64 8zM8 18h8v-2H8v2zm0-4h8v-6H8v6z"/>
            `;
    }

    deleteIcon.addEventListener("click", this._deleteImage.bind(this));

    return deleteIcon;
  }

  _deleteImage(e) {
    e.preventDefault();
    const parentDiv = e.target.closest(".image-container");
    const confirmation = confirm(
      "Are you sure you want to delete this Adj image?",
    );

    if (confirmation) {
      const fileName = e.currentTarget.getAttribute("data-filename");
      const fileKey = e.currentTarget.getAttribute("data-key");
      const blobId = e.currentTarget.getAttribute("data-id");

      fetch(`/delete_adj_image`, {
        method: "post",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          "X-CSRF-Token": document.head
            .querySelector('meta[name="csrf-token"]')
            .getAttribute("content"),
        },
        body: JSON.stringify({
          image_url: fileName,
          stack_id: this.gradingFormStackId,
          image_array: fileKey,
          blob_id: blobId,
        }),
      })
        .then((response) => {
          if (response.ok) {
            parentDiv.remove();
            location.reload();
          } else {
            console.error("Failed to delete image");
          }
        })
        .catch((error) => {
          console.error("Error:", error);
        });
    }
  }

  _setupLightbox(imageElement, imageUrl) {
    imageElement.addEventListener("click", () => {
      const lightboxImage = document.getElementById("lightbox-image");
      lightboxImage.src = imageUrl;
      const lightboxContainer = document.getElementById("lightbox-container");
      lightboxContainer.style.display = "block";
    });

    const lightboxContainer = document.getElementById("lightbox-container");
    lightboxContainer.addEventListener("click", () => {
      lightboxContainer.style.display = "none";
    });
  }

  appendCountInTheForm(counts) {
    this.gradingForm.components.forEach((component) => {
      if (component.components) {
        component.components.forEach((question) => {
          if (question.key === "marks_count_" + this.releaseEye) {
            const countComponent = document.querySelector(
              `.formio-component-${question.key}`,
            );
            if (countComponent) {
              const inputElement = countComponent.querySelector("input");
              if (inputElement) {
                // Set the value programmatically
                inputElement.value = counts;
                // Trigger an input event to notify the framework of the value change
                const event = new Event("input", { bubbles: true });
                inputElement.dispatchEvent(event);
              }
            }
          }
        });
      }
    });
  }

  appendGradingImageWithQuestion(component, questionId, fileUrls, filesArray) {
    if (
      component.element &&
      component.element.querySelector(".grading-screenshots") &&
      component.element.querySelector(".grading-files")
    ) {
      return; // If already appended, exit
    }

    const extractedData = filesArray.map((item) =>
      item.includes("<===>") ? item.split("<===>")[0].trim() : null,
    );
    const userNames = filesArray.map((item) =>
      item.includes("<===>") ? item.split("<===>")[1].trim() : null,
    );
    const gradingSpan = document.createElement("p");
    gradingSpan.classList.add("grading-screenshots");
    gradingSpan.textContent = "Grading Screenshots and Files";
    const addedFiles = new Set();
    const container = document.createElement("div");
    container.classList.add(
      "mt-1",
      "border",
      "rounded",
      "border-green-300",
      "flex",
      "gap-2",
      "grading-files",
    );

    const dropzoneElements = document.querySelectorAll(".dropzone");
    for (const key in fileUrls) {
      if (key === questionId) {
        for (let j = 0; j < fileUrls[key].length; j++) {
          for (let i = 0; i < extractedData.length; i++) {
            if (extractedData[i]) {
              let fileNameParts = extractedData[i].split("/");
              let encodedFileName = fileNameParts[fileNameParts.length - 1];
              let fileName = decodeURIComponent(encodedFileName);
              let userFileIdentifier = `${fileName}-${userNames[i]}`;

              if (
                fileUrls[key][j] === fileName &&
                !addedFiles.has(userFileIdentifier)
              ) {
                const fileExtension = fileName.split(".").pop().toLowerCase();
                const userName = userNames[i];
                const attachmentUrl = extractedData[i];
                const response = this.graderResponses[j];
                let valueToShow =
                  response?.responses?.data?.[questionId] || "N/A";
                if (fileExtension === "pdf") {
                  const pdfIconElement = document.createElement("img");
                  this.setAttributes(pdfIconElement, {
                    src: this.pdfIcon,
                    title: `Uploaded by: ${userName} | Answer: ${valueToShow}`,
                    width: "100",
                    height: "100",
                    alt: "Attachment PDF",
                    style: "cursor: pointer;",
                    draggable: "false", // Ensure the image is not draggable
                  });

                  const pdfLink = document.createElement("a");
                  this.setAttributes(pdfLink, {
                    href: attachmentUrl,
                    target: "_blank",
                    draggable: "true",
                    class: "pdf-link", // Adding a class for reference
                  });

                  // Attach the dragstart event to the PDF link (not the icon)
                  pdfLink.addEventListener("dragstart", function (event) {
                    event.dataTransfer.setData("text/plain", attachmentUrl); // PDF URL
                    event.dataTransfer.setData("filename", fileName); // Original file name
                  });

                  pdfLink.appendChild(pdfIconElement);

                  const fileContainer = document.createElement("div");
                  fileContainer.classList.add("file-container");
                  fileContainer.appendChild(pdfLink);

                  container.appendChild(fileContainer);
                } else {
                  const imageElement = document.createElement("img");
                  this.setAttributes(imageElement, {
                    src: attachmentUrl,
                    title: `Uploaded by: ${userName} | Answer: ${valueToShow}`,
                    draggable: true, // Prevent dragging image
                    width: "100",
                    height: "100",
                    alt: "Attachment Image",
                    style: "cursor: pointer;",
                  });

                  imageElement.addEventListener("click", function () {
                    const lightboxImage =
                      document.getElementById("lightbox-image");
                    const lightboxUsername =
                      document.getElementById("lightbox-username");
                    lightboxImage.src = attachmentUrl;
                    lightboxUsername.textContent = "Uploaded by: " + userName;
                    const lightboxContainer =
                      document.getElementById("lightbox-container");
                    lightboxContainer.style.display = "block";
                  });

                  const lightboxContainer =
                    document.getElementById("lightbox-container");
                  lightboxContainer.addEventListener("click", function () {
                    lightboxContainer.style.display = "none";
                    const lightboxUsername =
                      document.getElementById("lightbox-username");
                    lightboxUsername.textContent = "";
                  });

                  container.appendChild(imageElement);

                  imageElement.addEventListener("dragstart", function (event) {
                    event.dataTransfer.setData("text/plain", attachmentUrl);
                    event.dataTransfer.setData("filename", fileName);
                  });

                  container.appendChild(imageElement);
                }

                addedFiles.add(userFileIdentifier);
              }
            }
          }
        }
      }
    }

    // ✅ Ensure drop event is attached **only once**
    // Iterate over each dropzone
    dropzoneElements.forEach((dropzoneElement) => {
      const dropzone = Dropzone.forElement(dropzoneElement);

      if (!dropzoneElement.hasAttribute("data-dropzone-active")) {
        dropzoneElement.setAttribute("data-dropzone-active", "true");

        dropzoneElement.addEventListener("drop", async function (event) {
          event.preventDefault();
          let droppedUrl = event.dataTransfer.getData("text/plain"); // PDF URL
          let droppedFileName = event.dataTransfer.getData("filename"); // Original filename

          if (!droppedUrl || !droppedFileName) return; // Ignore if no file was dragged

          // Check if file already exists in this specific Dropzone
          let existingFiles = dropzone.files.map((file) => file.name);
          if (existingFiles.includes(droppedFileName)) {
            console.warn(
              "File already exists in Dropzone, skipping duplicate upload.",
            );
            return;
          }

          try {
            const response = await fetch(droppedUrl);
            const blob = await response.blob();

            const file = new File([blob], droppedFileName, { type: blob.type });

            // Add the dragged PDF file to this Dropzone
            dropzone.addFile(file);
          } catch (error) {
            console.error("Error fetching file:", error);
          }
        });
      }
    });

    if (
      container.innerHTML.trim() !== "" &&
      gradingSpan.innerHTML.trim() !== ""
    ) {
      if (component.element) {
        component.element.appendChild(container);
        component.element.appendChild(gradingSpan);
      }
    }
  }
  // to set attribute of icons or images
  setAttributes(element, attributes) {
    Object.entries(attributes).forEach(([key, value]) =>
      element.setAttribute(key, value),
    );
  }

  checkAdjudicationLogic(questionKey, originalForm) {
    // Iterate through the components in the originalForm
    for (const component of originalForm) {
      // Check if the current component has the specified questionKey
      for (const question of component["components"]) {
        if (question.key === questionKey) {
          // Check the adjudication for the component
          if (
            question.adjudication &&
            question.adjudication["status"] === "Required" &&
            question.adjudication["base_on"] === "Any Change"
          ) {
            return true;
          } else {
            return false;
          }
        }
      }
    }
  }

  appendSdvWithQuestionTitles(component, check_sdv, sdv_comments) {
    if (check_sdv === "true") {
      // Create a span element for the SVG icon with the tooltip
      const svgDive = document.createElement("span");
      svgDive.classList.add("has-tooltip");
      svgDive.innerHTML = `
          <?xml version="1.0" encoding="iso-8859-1"?>
            <!-- Uploaded to: SVG Repo, www.svgrepo.com, Generator: SVG Repo Mixer Tools -->
            <!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 1.1//EN" "http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd">
            <svg fill="#007BFF" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" 
                width="16px" height="16px" viewBox="0 0 509.604 509.604"
                xml:space="preserve" style ="margin-top:3px">
                <g>
                    <g>
                        <path d="M34.262,333.282c8.119,6.75,14.793,15.223,14.143,20.988c-0.382,3.443-0.593,6.943-0.593,10.5
                            c0,52.393,41.3,94.861,92.24,94.861c6.292,0,12.431-0.65,18.37-1.885c10.002-2.074,21.812,1.941,28.888,9.793
                            c16.82,18.646,40.803,30.342,67.492,30.342c28.19,0,53.426-13.016,70.342-33.518c6.723-8.146,18.103-11.533,28.22-8.5
                            c8.166,2.447,16.811,3.768,25.751,3.768c50.939,0,92.24-42.477,92.24-94.861c0-5.861-0.535-11.59-1.549-17.145
                            c-1.712-9.371,2.85-21.047,10.471-28.363c18.025-17.289,29.328-41.883,29.328-69.242c0-29.787-13.368-56.323-34.263-73.698
                            c-8.118-6.751-14.793-15.224-14.143-20.99c0.383-3.442,0.593-6.942,0.593-10.5c0-52.393-41.301-94.86-92.24-94.86
                            c-6.292,0-12.431,0.65-18.369,1.884c-10.002,2.075-21.812-1.941-28.889-9.792c-16.82-18.647-40.803-30.342-67.492-30.342
                            c-26.688,0-50.671,11.695-67.492,30.342c-7.076,7.841-18.886,11.867-28.888,9.792c-5.938-1.234-12.078-1.884-18.37-1.884
                            c-50.939,0-92.24,42.477-92.24,94.86c0,5.049,0.392,10.002,1.147,14.832c1.262,8.128-4.447,18.149-12.747,24.681
                            C14.219,201.663,0,228.887,0,259.583C0,289.37,13.368,315.907,34.262,333.282z M131.475,263.016
                            c2.046-3.625,7.268-3.672,12.049,0.479l48.119,33.918c2.61,1.588,5.106,2.4,7.506,2.4c4.963,0,8.893-3.576,12.689-7.02
                            l153.985-154.138c9.629-10.471,18.99-14.162,25.102-10.146c2.82,1.855,4.646,4.647,5.135,7.87
                            c0.583,3.825-0.756,7.946-3.768,11.599l-185.149,224.91c-2.687,3.26-6.11,5.059-9.629,5.059c-4.179,0-7.965-2.516-10.404-6.895
                            l-54.344-97.969C130.519,269.422,130.021,265.618,131.475,263.016z"/>
                    </g>
                </g>
            </svg>     
            <span class="tooltip bottom-2 w-min">${sdv_comments}</span>
          `;
      // Get the label element within component.element
      const labelElement = component.element.querySelector("label");
      labelElement.classList.add("flex", "gap-2");
      // Concatenate component.label and svgDive and set it as the innerHTML of labelElement
      labelElement.innerHTML = component.label + svgDive.outerHTML;
    }
  }

  loadExistingAnnotationsForImage(url) {
    // Extract filename from URL for matching (ActiveStorage URLs have changing tokens)
    const getFilename = (url) => {
      const parts = url.split("/");
      return parts[parts.length - 1]; // Get last part (filename)
    };

    const currentFilename = getFilename(url);

    let annotation = null;
    let matchedAnnotationUrl = url; // Default to current URL for cache key
    const isAdjudicator = this.annotationUsersElem.value === "adjudicator";
    const gradingEllipseData =
      this.gradingEllipseData?.[this.annotationUsersElem.value]?.[url];

    if (isAdjudicator) {
      // Try exact match first
      annotation = this.annotation?.[url] || null;

      // If no exact match, try matching by filename
      if (!annotation && this.annotation) {
        for (const annotationUrl in this.annotation) {
          const annotationFilename = getFilename(annotationUrl);
          if (annotationFilename === currentFilename) {
            annotation = this.annotation[annotationUrl];
            matchedAnnotationUrl = annotationUrl; // Use the matched URL for cache key
            break;
          }
        }
      }

      if (gradingEllipseData && Object.keys(gradingEllipseData).length > 0) {
        this.ellipseGrading.addEllipseRenderer();
      }
    } else {
      // Try exact match first
      annotation =
        this.graderAnnotations?.[this.annotationUsersElem.value]?.[url] || null;

      // If no exact match, try matching by filename
      if (
        !annotation &&
        this.graderAnnotations?.[this.annotationUsersElem.value]
      ) {
        const userAnnotations =
          this.graderAnnotations[this.annotationUsersElem.value];
        for (const annotationUrl in userAnnotations) {
          const annotationFilename = getFilename(annotationUrl);
          if (annotationFilename === currentFilename) {
            annotation = userAnnotations[annotationUrl];
            matchedAnnotationUrl = annotationUrl; // Use the matched URL for cache key
            break;
          }
        }
      }

      if (gradingEllipseData && Object.keys(gradingEllipseData).length > 0) {
        this.ellipseGrading.addEllipseRenderer();
      }
    }

    // Always clear previous annotations first for the current image URL
    cornerstoneTools.globalImageIdSpecificToolStateManager.clearImageIdToolState(
      url,
    );

    // Only restore annotation if it exists and hasn't been loaded before for this image
    // Check cache using the current URL (not the matched annotation URL) since we're loading for this specific image
    if (annotation && !this.annotationIsLoadedOnce[url]) {
      cornerstoneTools.globalImageIdSpecificToolStateManager.restoreImageIdToolState(
        url,
        annotation,
      );

      this.annotationIsLoadedOnce[url] = true;
      this.refreshTools();

      // Update circle count if probe data exists
      const probeData = annotation?.Probe?.data;
      this.circleCount = Array.isArray(probeData) ? probeData.length : 0;
      this.recalculateCircleCount(this.circleCount);
    } else if (!annotation) {
      // No annotation to load — reset circle count and refresh tools
      this.circleCount = 0;
      this.recalculateCircleCount(this.circleCount);
      this.refreshTools(); // Still refresh to show empty image
    }
  }

  recalculateCircleCount(circleCount) {
    // Trigger a custom event to recalculate circle count
    const recalculateEvent = new CustomEvent("recalculateCircleCount", {
      detail: { circleCount: this.circleCount },
    });
    this.viewerElement.dispatchEvent(recalculateEvent);
  }

  calculateAngle() {
    const annotations =
      cornerstoneTools.globalImageIdSpecificToolStateManager.saveToolState();
    let annotationsData = annotations
      ? annotations[Object.keys(annotations)[0]]
      : null;
    if (annotationsData) {
      let lengthData = annotationsData.Length;
      if (lengthData && lengthData.data.length === 2) {
        // Extracting Length data from annotationsData
        const annotationsData = lengthData.data.map((annotation) => ({
          visible: annotation.visible,
          active: annotation.active,
          invalidated: annotation.invalidated,
          handles: {
            start: annotation.handles.start,
            end: annotation.handles.end,
            textBox: annotation.handles.textBox,
          },
          uuid: annotation.uuid,
          length: annotation.length,
          unit: annotation.unit,
        }));
        fetch(this.apiUrl, {
          headers: {
            "X-API-KEY": this.apiKey,
            "Content-Type": "application/json; charset=utf-8",
          },
          method: "POST",
          body: JSON.stringify({
            Length: {
              data: annotationsData,
            },
            image_url: this.url,
            user_id: this.gradingFormUserId,
            stack_id: this.gradingFormStackId,
          }),
        })
          .then((response) => {
            response.status == 200 ? this.successAlert() : this.errorAlert();
            return response.json();
          })
          .then((data) => {
            document.querySelector('input[name="data[angle]"]').value =
              data.data.lens_rotation_degrees;
            document.querySelector('input[name="data[direction]"]').value =
              data.data.direction;
            this.gradingForm.submission.data.angle =
              "angle" in this.gradingForm.submission.data
                ? data.data.lens_rotation_degrees
                : data.data.lens_rotation_degrees;
            this.gradingForm.submission.data.direction =
              "direction" in this.gradingForm.submission.data
                ? data.data.direction
                : data.data.direction;
          });
      } else {
        alert("Two lines required to calculate angle");
      }
    } else {
      alert("Draw two lines through Length Tool to Calculate Angle");
    }
  }

  warningFunction() {
    Swal.fire({
      icon: "warning",
      title: "Please Upload screenshots!",
      showConfirmButton: true,
    });
  }

  uploadImageForQuestion(key) {
    Swal.fire({
      icon: "warning",
      title: "Please Upload screenshots for " + key,
      showConfirmButton: true,
    });
  }

  successAlert() {
    window.dispatchEvent(new CustomEvent("successAlert"));
  }

  errorAlert() {
    window.dispatchEvent(new CustomEvent("errorAlert"));
  }

  refreshTools() {
    const tools = [
      "Probe",
      "IOLProbe",
      "PupilProbe",
      "CorneaProbe",
      "ArrowAnnotate",
      "Angle",
      "EllipticalRoi",
      "RectangleRoi",
      "FreehandRoi",
      "Length",
      "Pan",
    ];
    tools.forEach((tool) => {
      cornerstoneTools.setToolActive(tool, { mouseButtonMask: LEFT_CLICK });
    });
  }

  readImages() {
    const images = document.querySelectorAll(".image");
    images.forEach((i) => {
      this.images.push({
        id: i.getAttribute("data-id"),
        url: i.getAttribute("data-url"),
        elem: i,
      });
    });
  }

  loadImage(url, id = null) {
    url = document.location.origin + "/" + url;
    cornerstone.loadAndCacheImage(url).then((image) => {
      this.loadExistingAnnotationsForImage(url);
      cornerstone.displayImage(this.viewerElement, image);
      cornerstone.enable(this.viewerElement);
    });
  }

  changeImage(e, elem) {
    let target;
    if (e) target = e.target;
    else target = elem;

    // Get the new image URL
    const newImageUrl = document.location.origin + "/" + target.dataset.url;

    // Clear annotation cache for the new image to ensure it loads fresh
    delete this.annotationIsLoadedOnce[newImageUrl];

    // load new image
    this.loadImage(target.dataset.url, target.dataset.id);

    // if the call came from click adjust the currentImage index
    if (e) {
      this.currentImage = target.dataset.idx;
      console.log(`current index seleted ${this.currentImage}`);
    }
    // remove all active borders
    const allImageBoxes = document.querySelectorAll(".image-box");
    allImageBoxes.forEach((i) => i.classList.remove("border-primary-500"));
    // active border around current image
    target.parentNode.classList.add("border-primary-500");

    // Update the data-adjudication-url attribute
    const gradeUrlElem = document.querySelector("[data-adjudication-url]");
    if (gradeUrlElem) {
      gradeUrlElem.setAttribute("data-adjudication-url", target.dataset.url);
    }

    // Now we can update `this.url` directly
    this.url = target.dataset.url;

    // Refresh ellipse rendering for the new image
    setTimeout(() => {
      this.ellipseGrading.addEllipseRenderer();
    }, 100);
  }

  toggle() {
    this.toggleModule.buttonEnable();
  }

  toggleEllipseData(e) {
    e.preventDefault();

    const type = e.currentTarget.dataset.type.toLowerCase(); // iol, pupil, cornea
    const toolMap = {
      iol: "IOLProbe",
      pupil: "PupilProbe",
      cornea: "CorneaProbe",
    };
    const toolName = toolMap[type];
    if (!toolName) return;

    const imageUrl = document.location.origin + "/" + this.url;

    // Extract filename from URL for matching (ActiveStorage URLs have changing tokens)
    const getFilename = (url) => {
      const parts = url.split("/");
      return parts[parts.length - 1]; // Get last part (filename)
    };

    const currentFilename = getFilename(imageUrl);

    // Determine which annotation source to use - match by filename
    let hasAnnotation = false;
    let annotationSource = null;

    if (this.annotation) {
      // Try exact match first
      if (this.annotation[imageUrl]) {
        hasAnnotation = true;
        annotationSource = this.annotation;
      } else {
        // Try matching by filename
        for (const annotationUrl in this.annotation) {
          const annotationFilename = getFilename(annotationUrl);
          if (annotationFilename === currentFilename) {
            hasAnnotation = true;
            annotationSource = this.annotation;
            break;
          }
        }
      }
    }

    const userKey = this.annotationUsersElem?.value;
    const userAnnotations = this.graderAnnotations?.[userKey];
    let hasGraderAnnotation = false;

    if (userAnnotations && this.usersTarget !== "adjudicator") {
      // Try exact match first
      if (userAnnotations[imageUrl]) {
        hasGraderAnnotation = true;
      } else {
        // Try matching by filename
        for (const annotationUrl in userAnnotations) {
          const annotationFilename = getFilename(annotationUrl);
          if (annotationFilename === currentFilename) {
            hasGraderAnnotation = true;
            break;
          }
        }
      }
    }

    if (!hasAnnotation && !hasGraderAnnotation) return;

    // Determine annotation source
    if (!annotationSource) {
      annotationSource = hasAnnotation
        ? this.annotation
        : this.graderAnnotations[this.annotationUsersElem.value];
    }

    // Initialize state tracker if not present
    if (!this.annotationToggleStates) {
      this.annotationToggleStates = { iol: 0, pupil: 0, cornea: 0 };
    }

    // Move to next state
    this.annotationToggleStates[type] =
      (this.annotationToggleStates[type] + 1) % 4;
    const currentState = this.annotationToggleStates[type];

    // Get annotation data - find by filename if exact match doesn't work
    let annotationData = annotationSource[imageUrl];
    if (!annotationData && annotationSource) {
      for (const annotationUrl in annotationSource) {
        const annotationFilename = getFilename(annotationUrl);
        if (annotationFilename === currentFilename) {
          annotationData = annotationSource[annotationUrl];
          break;
        }
      }
    }

    const toolData = annotationData?.[toolName];
    if (toolData && toolData.data) {
      if (currentState === 0) {
        // Show all
        toolData.data.forEach((a) => (a.visible = true));
        this.ellipseGrading.setVisibility(type, true);
      } else if (currentState === 1) {
        // Hide annotation, show ellipse
        toolData.data.forEach((a) => (a.visible = false));
        this.ellipseGrading.setVisibility(type, true);
      } else if (currentState === 2) {
        // Show annotation, hide ellipse
        toolData.data.forEach((a) => (a.visible = true));
        this.ellipseGrading.setVisibility(type, false);
      } else if (currentState === 3) {
        // Hide both
        toolData.data.forEach((a) => (a.visible = false));
        this.ellipseGrading.setVisibility(type, false);
      }

      // Update annotation view
      cornerstoneTools.globalImageIdSpecificToolStateManager.restoreImageIdToolState(
        imageUrl,
        annotationSource[imageUrl],
      );

      this.refreshTools();

      // Update button label
      const button = e.currentTarget;
      button.querySelector("span").textContent = this.getButtonLabel(
        type,
        currentState,
      );
    }
  }

  // Helper method to generate button labels
  getButtonLabel(type, state) {
    const typeLabels = {
      iol: "IOL",
      pupil: "PUPIL",
      cornea: "CORNEA",
    };

    const stateLabels = {
      0: "", // All visible
      1: "(N/P)", // Annotation hidden
      2: "(N/C)", // Ellipse hidden
      3: "(OFF)", // Both hidden
    };

    return `${typeLabels[type]} ${stateLabels[state]}`.trim();
  }

  nextImage(e) {
    const len = this.images.length;
    this.currentImage = Math.abs((this.currentImage + 1) % len);
    this.changeImage(null, this.images[this.currentImage].elem);
    e.preventDefault();
  }

  previousImage(e) {
    const len = this.images.length;
    this.currentImage = Math.abs(this.currentImage + len - 1) % len;
    this.changeImage(null, this.images[this.currentImage].elem);
    e.preventDefault();
  }

  changGradingUser(e) {
    const selectedUserId = this.annotationUsersElem.value;

    // Clear annotation cache to force reload
    this.annotationIsLoadedOnce = {};

    if (selectedUserId === "adjudicator") {
      // For adjudicator, find the image with the most recent processing (prioritize ellipse data timestamps)
      const adjudicatorEllipseData = this.ellipseGrading.ellipseData;
      const currentImageUrl = this.images[this.currentImage].url;
      const fullImageUrl = document.location.origin + "/" + currentImageUrl;

      let finalImageUrl = null;

      // First, try to find the image with the most recent ellipse data (based on timestamp)
      if (
        adjudicatorEllipseData &&
        Object.keys(adjudicatorEllipseData).length > 0
      ) {
        finalImageUrl = this.findFinalEllipseDataImage(adjudicatorEllipseData);

        // Check if current image has ellipse data (check both with and without origin)
        if (
          adjudicatorEllipseData[fullImageUrl] ||
          adjudicatorEllipseData[currentImageUrl]
        ) {
          // Compare timestamps - if current image is more recent, use it
          const currentEllipseTimestamp =
            this.getEllipseDataTimestamp(
              adjudicatorEllipseData,
              fullImageUrl,
            ) ||
            this.getEllipseDataTimestamp(
              adjudicatorEllipseData,
              currentImageUrl,
            ) ||
            0;
          const finalEllipseTimestamp =
            this.getEllipseDataTimestamp(
              adjudicatorEllipseData,
              finalImageUrl,
            ) || 0;

          if (currentEllipseTimestamp >= finalEllipseTimestamp) {
            // Current image has more recent or equal ellipse data, just reload it
            this.loadImage(currentImageUrl);
            setTimeout(() => {
              this.ellipseGrading.addEllipseRenderer();
            }, 200);
            return;
          }
        }
      }

      // If we have both ellipse data and annotations, compare timestamps to find the most recent
      if (this.annotation && Object.keys(this.annotation).length > 0) {
        const annotatedImageUrls = Object.keys(this.annotation);
        const finalAnnotatedImageUrl = this.findFinalAnnotatedImage(
          this.annotation,
          annotatedImageUrls,
        );

        if (finalImageUrl && finalAnnotatedImageUrl) {
          // Compare timestamps - ellipse data has timestamp in meta field
          const ellipseTimestamp =
            this.getEllipseDataTimestamp(
              adjudicatorEllipseData,
              finalImageUrl,
            ) || 0;
          const annotationTimestamp =
            this.annotation[finalAnnotatedImageUrl]?._timestamp || 0;

          // Use the one with the more recent timestamp
          if (annotationTimestamp > ellipseTimestamp) {
            finalImageUrl = finalAnnotatedImageUrl;
          }
        } else if (finalAnnotatedImageUrl) {
          finalImageUrl = finalAnnotatedImageUrl;
        }
      }

      // Find this image in our images array and switch to it
      if (finalImageUrl) {
        const imageToLoad = this.findImageByFullUrl(finalImageUrl);
        if (imageToLoad) {
          this.changeImage(null, imageToLoad.elem);
          return;
        }
      }

      // Final fallback: just reload current image
      this.loadImage(currentImageUrl);
      setTimeout(() => {
        this.ellipseGrading.addEllipseRenderer();
      }, 200);
    } else {
      // For graders, find the image with the most recent processing (prioritize ellipse data timestamps)
      const graderAnnotations = this.graderAnnotations?.[selectedUserId];
      const graderEllipseData = this.gradingEllipseData?.[selectedUserId];

      let finalImageUrl = null;

      // First, try to find the image with the most recent ellipse data (based on timestamp)
      if (graderEllipseData && Object.keys(graderEllipseData).length > 0) {
        finalImageUrl = this.findFinalEllipseDataImage(graderEllipseData);
      }

      // If we have both ellipse data and annotations, compare timestamps to find the most recent
      if (graderAnnotations && Object.keys(graderAnnotations).length > 0) {
        const annotatedImageUrls = Object.keys(graderAnnotations);
        const finalAnnotatedImageUrl = this.findFinalAnnotatedImage(
          graderAnnotations,
          annotatedImageUrls,
        );

        if (finalImageUrl && finalAnnotatedImageUrl) {
          // Compare timestamps - ellipse data has timestamp in meta field
          const ellipseTimestamp = this.getEllipseDataTimestamp(
            graderEllipseData,
            finalImageUrl,
          );
          const annotationTimestamp =
            graderAnnotations[finalAnnotatedImageUrl]?._timestamp || 0;

          // Use the one with the more recent timestamp
          if (annotationTimestamp > ellipseTimestamp) {
            finalImageUrl = finalAnnotatedImageUrl;
          }
        } else if (finalAnnotatedImageUrl) {
          finalImageUrl = finalAnnotatedImageUrl;
        }
      }

      // Find this image in our images array and switch to it
      if (finalImageUrl) {
        const imageToLoad = this.findImageByFullUrl(finalImageUrl);

        if (imageToLoad) {
          // Switch to this image
          this.changeImage(null, imageToLoad.elem);
          // Note: changeImage already handles ellipse rendering with a timeout
        } else {
          console.warn(`Could not find image for grader ${selectedUserId}`);
          // Fallback: reload current image
          const currentImageUrl = this.images[this.currentImage].url;
          this.loadImage(currentImageUrl);
          // Defer ellipse rendering until image is loaded
          setTimeout(() => {
            this.ellipseGrading.addEllipseRenderer();
          }, 100);
        }
      } else {
        // No annotations or ellipse data for this grader, just reload current image
        const currentImageUrl = this.images[this.currentImage].url;
        this.loadImage(currentImageUrl);
        // Defer ellipse rendering until image is loaded
        setTimeout(() => {
          this.ellipseGrading.addEllipseRenderer();
        }, 200);
      }
    }
  }

  // Helper method to find the final (most recently annotated) image
  findFinalAnnotatedImage(graderAnnotations, annotatedImageUrls) {
    let latestImageUrl = null;
    let latestTimestamp = 0;

    // Check each annotated image for timestamp at the image level
    for (const imageUrl of annotatedImageUrls) {
      const imageAnnotations = graderAnnotations[imageUrl];

      // Check for image-level timestamp
      if (imageAnnotations && imageAnnotations._timestamp) {
        const timestamp = imageAnnotations._timestamp;

        if (timestamp > latestTimestamp) {
          latestTimestamp = timestamp;
          latestImageUrl = imageUrl;
        }
      }
    }

    // If no timestamps found, fall back to checking which image matches this.images order
    // The assumption is that this.images is ordered by processing sequence

    if (!latestImageUrl || latestTimestamp === 0) {
      // Try to find the image that appears last in this.images array
      let lastFoundIndex = -1;

      for (const imageUrl of annotatedImageUrls) {
        const imageData = this.findImageByFullUrl(imageUrl);
        if (imageData) {
          const index = this.images.indexOf(imageData);
          if (index > lastFoundIndex) {
            lastFoundIndex = index;
            latestImageUrl = imageUrl;
          }
        }
      }
    }

    // Final fallback: just return the last URL in the array
    return latestImageUrl || annotatedImageUrls[annotatedImageUrls.length - 1];
  }

  // Helper method to find the final (most recently processed) ellipse data image
  findFinalEllipseDataImage(ellipseData) {
    let latestImageUrl = null;
    let latestTimestamp = 0;

    // Check each image's ellipse data for timestamp
    for (const imageUrl in ellipseData) {
      const imageEllipseData = ellipseData[imageUrl];

      // ellipseData[imageUrl] is an array, check the first element's meta.timestamp
      if (
        imageEllipseData &&
        Array.isArray(imageEllipseData) &&
        imageEllipseData.length > 0
      ) {
        const firstEllipse = imageEllipseData[0];
        if (firstEllipse && firstEllipse.meta && firstEllipse.meta.timestamp) {
          // Parse timestamp string (format: "YYYY-MM-DD-HH:MM:SS-UTC")
          try {
            const timestampStr = firstEllipse.meta.timestamp;
            const timestamp = new Date(
              timestampStr.replace(
                /(\d{4})-(\d{2})-(\d{2})-(\d{2}):(\d{2}):(\d{2})-UTC/,
                "$1-$2-$3T$4:$5:$6Z",
              ),
            ).getTime();

            if (timestamp > latestTimestamp) {
              latestTimestamp = timestamp;
              latestImageUrl = imageUrl;
            }
          } catch (e) {
            // If timestamp parsing fails, skip this image
            console.warn("Failed to parse ellipse data timestamp:", e);
          }
        }
      }
    }

    // If no timestamps found, fall back to last image in the object
    if (!latestImageUrl || latestTimestamp === 0) {
      const imageUrls = Object.keys(ellipseData);
      if (imageUrls.length > 0) {
        latestImageUrl = imageUrls[imageUrls.length - 1];
      }
    }

    return latestImageUrl;
  }

  // Helper method to get ellipse data timestamp for a specific image
  getEllipseDataTimestamp(ellipseData, imageUrl) {
    const imageEllipseData = ellipseData[imageUrl];

    if (
      imageEllipseData &&
      Array.isArray(imageEllipseData) &&
      imageEllipseData.length > 0
    ) {
      const firstEllipse = imageEllipseData[0];
      if (firstEllipse && firstEllipse.meta && firstEllipse.meta.timestamp) {
        try {
          const timestampStr = firstEllipse.meta.timestamp;
          return new Date(
            timestampStr.replace(
              /(\d{4})-(\d{2})-(\d{2})-(\d{2}):(\d{2}):(\d{2})-UTC/,
              "$1-$2-$3T$4:$5:$6Z",
            ),
          ).getTime();
        } catch (e) {
          return 0;
        }
      }
    }

    return 0;
  }

  // Helper method to find image by full URL (with filename fallback for ActiveStorage token changes)
  findImageByFullUrl(fullUrl) {
    // fullUrl can come as:
    // 1. "http://origin/path/to/image.jpg" (with origin)
    // 2. "/path/to/image.jpg" (without origin)
    // this.images[].url is like: "path/to/image.jpg"

    // Extract filename from URL for matching (ActiveStorage URLs have changing tokens)
    const getFilename = (url) => {
      const parts = url.split("/");
      return parts[parts.length - 1]; // Get last part (filename)
    };

    const targetFilename = getFilename(fullUrl);

    // Extract the path part from fullUrl if it includes origin
    let urlPath = fullUrl;
    if (fullUrl.startsWith("http://") || fullUrl.startsWith("https://")) {
      try {
        const url = new URL(fullUrl);
        urlPath = url.pathname;
      } catch (e) {
        console.error("Error parsing URL:", e);
      }
    }

    // Remove leading slash from urlPath if present for comparison
    urlPath = urlPath.startsWith("/") ? urlPath.substring(1) : urlPath;

    for (let i = 0; i < this.images.length; i++) {
      const imageUrl = this.images[i].url.startsWith("/")
        ? this.images[i].url.substring(1)
        : this.images[i].url;
      const imageFullUrl = document.location.origin + "/" + this.images[i].url;

      // Try to match against different formats
      if (
        imageFullUrl === fullUrl ||
        this.images[i].url === fullUrl ||
        imageUrl === urlPath
      ) {
        return this.images[i];
      }

      // If exact match fails, try matching by filename (for ActiveStorage token changes)
      const imageFilename = getFilename(imageFullUrl);
      if (imageFilename === targetFilename) {
        return this.images[i];
      }
    }
    return null;
  }

  /**
   *
   * TODO: Remove function and UI. this feature is not being ued currently
   */
  labelTool(e) {
    const tools = [
      "Length",
      "ArrowAnnotate",
      "Angle",
      "EllipticalRoi",
      "RectangleRoi",
      "FreehandRoi",
      "Probe",
      "IOLProbeTool",
      "PupilProbeTool",
      "CorneaProbeTool",
    ];

    tools.forEach((tool) => {
      cornerstoneTools.setToolEnabled(tool, { mouseButtonMask: LEFT_CLICK });
    });

    this.viewerElement.addEventListener("cornerstonetoolsmousedown", (e) => {
      const coord = e.detail.currentPoints.canvas;
      const mouseBtn = e.detail.buttons;
      // if its a right click
      if (mouseBtn === 2) {
        tools.forEach((tool) => {
          const state = cornerstoneTools.getToolState(this.viewerElement, tool);
          if (state) {
            state.data.forEach((d) => {
              const isNear = cornerstoneTools
                .getToolForElement(this.viewerElement, tool)
                .__proto__.pointNearTool(this.viewerElement, d, coord);
              if (isNear)
                console.log(`${tool} with ${d.uuid} near : ${isNear}`);
              cornerstoneTools.setToolActive(tool, {
                mouseButtonMask: LEFT_CLICK,
              });
              // when near is detected see if we can have the annotation highlited green
            });
          }
        });
      }
    });
    e.preventDefault();
  }

  levelsTool(e) {
    cornerstoneTools.setToolActive("Wwwc", { mouseButtonMask: LEFT_CLICK });
    e.preventDefault();
  }

  panTool(e) {
    cornerstoneTools.setToolActive("Pan", { mouseButtonMask: LEFT_CLICK });
    e.preventDefault();
  }

  lengthTool(e) {
    cornerstoneTools.setToolActive("Length", { mouseButtonMask: LEFT_CLICK });
    e.preventDefault();
  }

  arrowAnnotationTool(e) {
    cornerstoneTools.setToolActive("ArrowAnnotate", {
      mouseButtonMask: LEFT_CLICK,
    });
    e.preventDefault();
  }

  angleTool(e) {
    cornerstoneTools.setToolActive("Angle", { mouseButtonMask: LEFT_CLICK });
    e.preventDefault();
  }

  probeTool(e) {
    e.preventDefault();

    const probeType = e.currentTarget.dataset.probeType;
    let toolName;

    // Determine tool name based on probeType
    switch (probeType) {
      case "iol":
        toolName = "IOLProbe";
        break;
      case "pupil":
        toolName = "PupilProbe";
        break;
      case "cornea":
        toolName = "CorneaProbe";
        break;
      default:
        toolName = "Probe";
        break;
    }

    this.setSelectedTool(toolName);
    cornerstoneTools.setToolActive(toolName, {
      mouseButtonMask: 1,
      disableTextBox: true,
    });
  }

  setSelectedTool(toolName) {
    this.selectedTool = toolName;
  }

  rectangleTool(e) {
    cornerstoneTools.setToolActive("RectangleRoi", {
      mouseButtonMask: LEFT_CLICK,
    });
    e.preventDefault();
  }

  freehandTool(e) {
    cornerstoneTools.setToolActive("FreehandRoi", {
      mouseButtonMask: LEFT_CLICK,
    });
    e.preventDefault();
  }

  ellipseTool(e) {
    cornerstoneTools.setToolActive("EllipticalRoi", {
      mouseButtonMask: LEFT_CLICK,
    });
    e.preventDefault();
  }

  eraserTool(e) {
    cornerstoneTools.setToolActive("Eraser", { mouseButtonMask: LEFT_CLICK });
    e.preventDefault();
  }

  async submitGrading(e) {
    e.preventDefault();
    this.notifications.showLoader();
    const processButtons = document.getElementsByClassName("processButton");
    const processButton = processButtons[0]; // Get the first element with class "processButton"
    if (
      processButton &&
      processButton.classList &&
      processButton.classList.contains("bg-red-600")
    ) {
      processButton.classList.remove("bg-red-600");
      processButton.classList.add("bg-purple-800");
    }
    const tools = [
      "Length",
      "ArrowAnnotate",
      "Angle",
      "EllipticalRoi",
      "RectangleRoi",
      "FreehandRoi",
      "Probe",
      "IOLProbe",
      "PupilProbe",
      "CorneaProbe",
    ];

    const annotations =
      cornerstoneTools.globalImageIdSpecificToolStateManager.saveToolState();
    const button = document.getElementsByClassName("iolDecentrationAdj")[0];
    const isChecked = button?.getAttribute("aria-checked") === "true";
    const imageUrl = document.location.origin + "/" + this.url;
    try {
      let ellipseData = null;

      if (isChecked) {
        // get ellipseData first
        ellipseData = await this.ellipseGrading.designEllipse(
          annotations,
          imageUrl,
        );
        await this.appendValues(ellipseData);
        this.playWithEllipseDataBottons(annotations, ellipseData);
      }

      // Update this.annotation with the latest annotations
      this.annotation = this.annotation || {};
      this.annotation[imageUrl] = annotations[imageUrl] || {};

      // Add timestamp to track when this image was annotated
      if (this.annotation[imageUrl]) {
        this.annotation[imageUrl]._timestamp = Date.now();
      }

      await this.formSaveDraft(e, true); // Skip validation for IOL processing
      this.saveGrading(annotations, ellipseData);
    } catch (error) {
      this.notifications.hideLoader();
      console.error("Grading submission failed:", error.message);
    }
  }

  async appendValues(ellipseData) {
    if (!ellipseData || !this.url || !ellipseData[this.url]) {
      console.warn("Missing required data in ellipseData");
      return;
    }

    const dataSource = ellipseData[this.url][0];

    const notifyValidation = (component, value) => {
      if (this.previousVisitValidationModule) {
        this.previousVisitValidationModule.afterProgrammaticValueSet(
          component,
          value,
        );
      }
    };

    // Loop through all components in the grading form
    this.gradingForm.everyComponent((component) => {
      const dataApiKey = component.component?.attributes?.["data-api"];
      const shouldRound =
        component.component?.attributes?.["data-round"] === "true";

      if (!dataApiKey) return;

      if (dataSource && dataSource[dataApiKey] !== undefined) {
        let valueToSet = dataSource[dataApiKey];

        // Format IOL_Pupil_Distance to 2 decimal places
        if (
          dataApiKey === "IOL_Pupil_Distance" &&
          typeof valueToSet === "number"
        ) {
          // Round to 2 decimal places
          const roundedValue = Math.round(valueToSet * 100) / 100;
          valueToSet = roundedValue;

          // Try to format the display to show 2 decimal places
          try {
            component.setValue(roundedValue);
            // If it's a textfield, set the formatted string
            if (component.component.type === "textfield") {
              component.setValue(roundedValue.toFixed(2));
              notifyValidation(component, roundedValue.toFixed(2));
            } else if (component.refs?.input && component.refs.input[0]) {
              // For number fields, try to update the display value
              const input = component.refs.input[0];
              if (input && input.type === "text") {
                input.value = roundedValue.toFixed(2);
              }
              notifyValidation(component, roundedValue);
            } else {
              notifyValidation(component, roundedValue);
            }
          } catch (e) {
            console.warn(`Failed to format value for ${dataApiKey}:`, e);
          }
          return; // Skip the normal setValue below
        }
        // Round the value if data-round is true and it's a number
        else if (shouldRound && typeof valueToSet === "number") {
          valueToSet = Math.round(valueToSet); // or Math.floor(valueToSet)
        }

        try {
          component.setValue(valueToSet);
          notifyValidation(component, valueToSet);
        } catch (e) {
          console.warn(`Failed to set value for ${dataApiKey}:`, e);
        }
      } else {
        console.warn(`Data for ${dataApiKey} not found in ellipseData`);
      }
    });
  }

  screenshotTool(event) {
    event.preventDefault();

    const viewer = this.viewerTarget;

    html2canvas(viewer, {
      useCORS: true, // In case of cross-origin images
      backgroundColor: null, // Preserves transparency if needed
    })
      .then((canvas) => {
        const image = canvas.toDataURL("image/png");

        // Optional: Trigger download
        const link = document.createElement("a");
        link.href = image;
        link.download = `${this.filename}.png`;
        link.click();
      })
      .catch((error) => {
        console.error("Screenshot failed:", error);
      });
  }

  downloadAnnotation(event) {
    event.preventDefault();
    const annotations = this.annotation;
    const ellipseData = this.ellipseData;

    const imageUrl = document.location.origin + "/" + this.url;

    if (!imageUrl || typeof imageUrl !== "string" || imageUrl.trim() === "") {
      alert("Invalid image URL provided.");
      return;
    }

    const imageAnn = annotations[imageUrl];

    if (!imageAnn) {
      alert("No annotation data found for this image URL.");
      return;
    }

    const result = {
      IOL: [],
      Pupil: [],
      Cornea: [],
      IOL_diameter: this.data.get("iolDiameter"),
    };

    if (imageAnn?.IOLProbe?.data) {
      imageAnn.IOLProbe.data.forEach((m) => {
        if (m.handles?.end) {
          result.IOL.push([
            Math.round(m.handles.end.x),
            Math.round(m.handles.end.y),
          ]);
        }
      });
    }

    if (imageAnn?.PupilProbe?.data) {
      imageAnn.PupilProbe.data.forEach((m) => {
        if (m.handles?.end) {
          result.Pupil.push([
            Math.round(m.handles.end.x),
            Math.round(m.handles.end.y),
          ]);
        }
      });
    }

    if (imageAnn?.CorneaProbe?.data) {
      imageAnn.CorneaProbe.data.forEach((m) => {
        if (m.handles?.end) {
          result.Cornea.push([
            Math.round(m.handles.end.x),
            Math.round(m.handles.end.y),
          ]);
        }
      });
    }

    // Send data to Rails controller for CSV generation
    this.exportToTxtZip(result, ellipseData[this.url][0]);
  }

  async exportToTxtZip(inputData, outputData) {
    try {
      // Get CSRF token
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");

      // Prepare form data
      const formData = new FormData();
      formData.append("input_data", JSON.stringify(inputData));
      formData.append("output_data", JSON.stringify(outputData));
      formData.append("filename", this.filename);

      // Make the request
      const response = await fetch(this.donwloadUrl, {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken,
          Accept: "application/zip",
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Get the ZIP content
      const zipBlob = await response.blob();

      // Create download link and trigger download
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      const now = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
      const randomDigits = Math.floor(100 + Math.random() * 900); // Generates a 3-digit random number
      link.download = `Input_output_${now}_${randomDigits}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log("ZIP download completed successfully");
    } catch (error) {
      console.error("Error downloading ZIP:", error);
      alert("Failed to download ZIP. Please try again.");
    }
  }

  playWithEllipseDataBottons(annotations, ellipseData = null) {
    const elementsWithData = document.querySelectorAll(
      "[data-adjudication-ellipse-data]",
    );
    if (elementsWithData.length > 0) {
      elementsWithData.forEach((element) => {
        // Update the data attributes
        element.setAttribute(
          "data-adjudication-annotation",
          JSON.stringify(annotations),
        );
        element.setAttribute(
          "data-adjudication-ellipse-data",
          ellipseData ? JSON.stringify(ellipseData) : "{}",
        );
      });
    }
    const ellipseButtons = document.querySelectorAll(".ellipse-data-button");
    ellipseButtons.forEach((button) => {
      if (!ellipseData || JSON.stringify(ellipseData) === "{}") {
        button.classList.add("hidden");
      } else {
        button.classList.remove("hidden");
      }
    });
  }

  saveGrading(annotations, ellipseData = null) {
    const payload = {
      adjudication: {
        annotations: annotations,
      },
    };

    if (ellipseData !== null) {
      payload.adjudication.ellipse_data = ellipseData;
    }

    fetch(this.gradingSaveUrl, {
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-CSRF-Token": document.head
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      method: "PATCH",
      body: JSON.stringify(payload),
    })
      .then(async (response) => {
        // Refresh initials after saving annotations
        await this.addGraderInitialsToThumbnails();

        // Set dropdown to adjudicator after processing
        // Only change if not already set to adjudicator
        if (
          this.annotationUsersElem &&
          this.annotationUsersElem.value !== "adjudicator"
        ) {
          this.annotationUsersElem.value = "adjudicator";
          // Clear annotation cache to force reload
          this.annotationIsLoadedOnce = {};
          // Reload annotations for current image (stay on same image)
          const currentImageUrl = this.images[this.currentImage].url;
          const fullImageUrl = document.location.origin + "/" + currentImageUrl;
          this.loadExistingAnnotationsForImage(fullImageUrl);
          setTimeout(() => {
            this.ellipseGrading.addEllipseRenderer();
          }, 200);
        }
        // console.log(response);
      })
      .catch((error) => {
        console.error("Grading save failed:", error);
      })
      .finally(() => {
        this.notifications.hideLoader();
      });
  }

  get url() {
    return this.data.get("url");
  }

  set url(value) {
    this.data.set("url", value);
  }

  async defaultSaveAnnotation(e) {
    try {
      const tools = [
        "Length",
        "ArrowAnnotate",
        "Angle",
        "EllipticalRoi",
        "RectangleRoi",
        "FreehandRoi",
        "Probe",
        "IOLProbe",
        "PupilProbe",
        "CorneaProbe",
      ];
      const annotations =
        cornerstoneTools.globalImageIdSpecificToolStateManager.saveToolState();
      const button = document.getElementsByClassName("iolDecentrationAdj")[0];
      const isChecked = button?.getAttribute("aria-checked") === "true";
      const imageUrl = document.location.origin + "/" + this.url;
      let ellipseData = null;
      this.annotation = this.annotation || {};
      this.annotation[imageUrl] = annotations[imageUrl] || {};

      if (isChecked) {
        // Get ellipseData and wait for it
        ellipseData = await this.ellipseGrading.designEllipse(
          annotations,
          imageUrl,
        );
        await this.appendValues(ellipseData);
        this.playWithEllipseDataBottons(annotations, ellipseData);
      }

      // Save grading (assuming this is synchronous; if async, it should be awaited)
      this.saveGrading(annotations, ellipseData);

      // Explicitly resolve the Promise
      return Promise.resolve();
    } catch (error) {
      // Log and reject with the error
      console.error("Error in defaultSaveAnnotation:", error);
      return Promise.reject(error);
    }
  }

  // formio functions
  formSaveDraft(e, skipValidation = false) {
    return this.handleFormCommitAndSubmit(
      e,
      "initiated",
      true,
      true,
      skipValidation,
    );
  }

  formCommit(e) {
    if (this.gradingFormAgreementElem?.checked) {
      this.handleFormCommitAndSubmit(e, "completed", true);
      this.notifications.showLoader();
      // Refresh initials after commit to show adjudicator's work
      setTimeout(async () => {
        await this.addGraderInitialsToThumbnails();
      }, 1000);
    } else {
      alert("You must agree to terms");
    }
    e.preventDefault();
  }

  formUpdate(e) {
    this.handleFormCommitAndSubmit(e, "editing");
  }

  handleFormCommitAndSubmit(
    e,
    status,
    shouldSubmit = false,
    draftStatus = false,
    skipValidation = false,
  ) {
    e.preventDefault();

    // If skipValidation is true, bypass form validation and use current form data
    if (skipValidation) {
      // Collect all current form values from all components without validation
      const currentData = {};
      this.gradingForm.everyComponent((component) => {
        if (component.component && component.component.key) {
          try {
            const value = component.getValue();
            if (value !== undefined && value !== null && value !== "") {
              currentData[component.component.key] = value;
            } else if (value === "" || value === null) {
              // Include empty values too to ensure we save all form state
              currentData[component.component.key] = value;
            }
          } catch (error) {
            // If getValue fails, try to get from component data
            if (component.data && component.data.value !== undefined) {
              currentData[component.component.key] = component.data.value;
            }
          }
        }
      });

      // Merge with existing submission to preserve metadata
      const existingSubmission = this.gradingForm.submission || {};

      // Construct submission object with all current form data
      const submission = {
        data: {
          ...existingSubmission.data,
          ...currentData, // Current form values override existing ones
        },
        isAgreed: existingSubmission.isAgreed || false,
        ...existingSubmission,
      };

      // Collect images
      this.formSubmissionModule.collectImagesFromSelector(
        ".attached_images",
        this.adjImages,
      );

      // Note: We don't call defaultSaveAnnotation here when skipValidation is true
      // because the calling method (submitGrading) will handle saving annotations
      // and ellipse data separately via saveGrading()

      // Submit the form without validation - saves all current data to database
      submission.previous_visit_overrides = this.previousVisitValidationModule
        ? this.previousVisitValidationModule.getOverrides()
        : {};
      return this.formSubmissionModule.submitForm(
        submission,
        "PATCH",
        null,
        null,
        draftStatus,
        "adjudication",
        status,
      );
    }

    // Validate the form before submission
    this.gradingForm
      .submit()
      .then((submission) => {
        // Assuming you have this.gradingImages defined in your controller
        this.formSubmissionModule.collectImagesFromSelector(
          ".attached_images",
          this.adjImages,
        );
        // Ensure defaultSaveAnnotation is a Promise and wait for it to resolve
        return this.defaultSaveAnnotation(e).then(() => {
          submission.previous_visit_overrides = this
            .previousVisitValidationModule
            ? this.previousVisitValidationModule.getOverrides()
            : {};
          // Handle the form submission after defaultSaveAnnotation is successful
          return this.formSubmissionModule.submitForm(
            submission,
            "PATCH",
            null,
            null,
            draftStatus,
            "adjudication",
            status,
          );
        });
      })
      .catch((error) => {
        console.error(error);
        // Handle any additional error logic here if needed
      });
  }

  regrade(e) {
    this.regradingFormElem?.classList.toggle("hidden");
    e.preventDefault();
  }

  confirmRegrade(e) {
    const markedForRegrade = this.regradersElems
      .filter((c) => c.checked)
      .map((c) => c.getAttribute("data-user"));
    if (markedForRegrade.length) {
      this.saveRegradeRequest(markedForRegrade);
    }
    e.preventDefault();
  }

  // TODO: Refactor and extract all the ajax calls (3 in this controller) to a common utility and DRY it up
  saveRegradeRequest(userIDs) {
    if (userIDs && userIDs.length) {
      fetch(this.regradingUrl, {
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          "X-CSRF-Token": document.head
            .querySelector('meta[name="csrf-token"]')
            .getAttribute("content"),
        },
        method: "PATCH",
        body: JSON.stringify({
          stack: {
            user_ids: userIDs,
          },
        }),
      }).then((response) => {
        this.notifications.success("Forms are open to regrade now!");
      });
    } else {
      console.log("No users were selected for regrading");
    }
  }

  toggleImage() {
    if (this.cornerstoneViewer.dataset.expanded === "1") {
      this.collapse_cornerstone();
    } else {
      this.expand_cornerstone();
    }
  }

  collapse_cornerstone() {
    const grading_tools =
      this.cornerstoneViewer.querySelector(".grading_tools");
    this.cornerstoneViewer.classList.add("col-span-1");
    this.cornerstoneViewer.classList.remove("col-span-6");
    grading_tools.classList.add("hidden");
    this.viewerTarget.classList.add("hidden");
    this.cornerstoneViewer.dataset.expanded = "0";
    this.cornerstoneIconTarget.classList.add("p-2");
    this.cornerstoneIconTarget.classList.add("rounded-sm");
    this.cornerstoneIconTarget.classList.add("text-white");
    this.cornerstoneIconTarget.innerHTML = `VIEWER`;
    this.largeForm();
  }

  largeForm() {
    this.formSection.classList.add("col-span-10");
    this.formSection.classList.remove("col-span-5");
  }

  expand_cornerstone() {
    const grading_tools =
      this.cornerstoneViewer.querySelector(".grading_tools");
    this.cornerstoneViewer.classList.add("col-span-6");
    this.cornerstoneViewer.classList.remove("col-span-1");
    grading_tools.classList.remove("hidden");
    this.viewerTarget.classList.remove("hidden");
    this.cornerstoneViewer.dataset.expanded = "1";
    this.cornerstoneIconTarget.classList.remove("p-2");
    this.cornerstoneIconTarget.classList.remove("rounded-sm");
    this.cornerstoneIconTarget.classList.remove("text-white");
    this.cornerstoneIconTarget.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="white">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
    </svg>`;
    this.reversLargeForm();
  }

  reversLargeForm() {
    this.formSection.classList.remove("col-span-10");
    this.formSection.classList.add("col-span-5");
  }

  toggleSubmitArea() {
    var staticPanel = document.getElementById("expend_colapse");
    if (this.questionAreaTarget.classList.contains("hidden")) {
      this.questionAreaTarget.classList.remove("hidden");
      staticPanel.classList.add("fa-minus-square-o");
      staticPanel.classList.remove("fa-plus-square-o");
    } else {
      this.questionAreaTarget.classList.add("hidden");
      staticPanel.classList.add("fa-plus-square-o");
      staticPanel.classList.remove("fa-minus-square-o");
    }
  }

  fetchPreviousVisitAnswers() {
    event.preventDefault();
    const method = "POST";
    fetch(this.gradingPreviousUrl, {
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.head
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      method: method,
      body: JSON.stringify({
        stack_id: this.gradingFormStackId,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        if (data.message == "Found") {
          if (!data || typeof data !== "object") {
            throw new Error("Invalid JSON data received");
          }
          const previousGradingFormData = data.responses.concat(
            data.history_reponse,
          );
          const userIDs = data.user_names;
          const previousVisitLabel = data.previous_visit_label;
          const previousVisitDate = data.previous_date;
          const formStatuses = data.status;
          const visitDates = data.history_visit_dates;
          const visitIds = data.history_visits_ids;
          const previousStatusArray = Array.isArray(data.previous_status)
            ? data.previous_status
            : [];
          if (data.responses.length !== userIDs.length) {
            console.error(
              "Length mismatch between previousGradingFormData and userIDs",
            );
          } else {
            if (previousGradingFormData.length > 0) {
              const firstPreviousStatus =
                previousStatusArray.length > 0 ? previousStatusArray[0] : null;
              this.appendPreviousVisitAnsToGradingForm(
                previousGradingFormData[0],
                firstPreviousStatus,
                previousVisitLabel,
                previousVisitDate,
              );
            }

            for (let i = 1; i < previousGradingFormData.length; i++) {
              const visitDate =
                visitDates.length > i - 1 ? visitDates[i - 1] : null;
              const visitId = visitIds.length > i - 1 ? visitIds[i - 1] : null;
              const formStatus =
                formStatuses.length > i - 1 ? formStatuses[i - 1] : null;
              this.appendPreviousVisitAnsToGradingForm(
                previousGradingFormData[i],
                null,
                previousVisitLabel,
                previousVisitDate,
                formStatus,
                visitDate,
                visitId,
                true,
              );
            }
          }

          document
            .getElementById("viewPreviousAnsButton")
            .classList.add("hidden");
          document
            .getElementById("hidePreviousAnsButton")
            .classList.remove("hidden");
        } else if (data.message == "No previous visit Found") {
          alert("No previous visit Available");
        }
      })
      .catch((error) => {
        console.error("There was a problem with the fetch operation:", error);
      });
  }

  hidePreviousAns() {
    event.preventDefault();

    // Remove the appended data by removing the .adj-answers-div elements
    const graderAnswersDivs = document.querySelectorAll(".adj-answers-div");
    graderAnswersDivs.forEach((div) => {
      div.remove();
    });

    // Show the "View Previous Ans" button
    const viewPreviousAnsButton = document.getElementById(
      "viewPreviousAnsButton",
    );
    viewPreviousAnsButton.classList.remove("hidden");

    // Hide the "Hide Previous Ans" button
    const hidePreviousAnsButton = document.getElementById(
      "hidePreviousAnsButton",
    );
    hidePreviousAnsButton.classList.add("hidden");
  }

  showLongitudinalChangesReport(event) {
    if (event && typeof event.preventDefault === "function")
      event.preventDefault();
    const overrides =
      (this.gradingFormResponses &&
        this.gradingFormResponses.previous_visit_overrides) ||
      {};
    PreviousVisitValidationModule.openLongitudinalChangesReportModal(
      this.gradingFormSchema,
      overrides,
      (schema, questionKey, val) =>
        this.getLabelFromSchema(schema, questionKey, val),
    );
  }

  appendPreviousVisitAnsToGradingForm(
    previousGradingFormData,
    previousStatus,
    previousVisitLabel,
    previousVisitDate,
    formStatus,
    visitDate,
    visitId,
    isSubsequentCall = false,
  ) {
    FormioUtils.eachComponent(this.gradingForm.components, (c) => {
      if (
        c &&
        c.component &&
        c.element &&
        !c.element.classList.contains("formio-hidden")
      ) {
        let graderAnswersDiv = c.element.querySelector(".adj-answers-div");
        if (!graderAnswersDiv) {
          graderAnswersDiv = document.createElement("div");
          graderAnswersDiv.classList.add("adj-answers-div");
          c.element.appendChild(graderAnswersDiv);
        }

        const questionKey = c.component.key;

        if (previousGradingFormData.data.hasOwnProperty(questionKey)) {
          const label =
            this.getLabelFromSchema(
              this.gradingFormSchema,
              questionKey,
              previousGradingFormData.data[questionKey],
            ) || "nil";
          const timestamp = new Date(previousVisitDate);
          const formattedDate = `${timestamp.getFullYear()}-${String(
            timestamp.getMonth() + 1,
          ).padStart(2, "0")}-${String(timestamp.getDate()).padStart(2, "0")}`;
          let currentFormStatus = formStatus;
          if (currentFormStatus === "completed") {
            currentFormStatus = "[a]";
          } else if (currentFormStatus === "editing") {
            currentFormStatus = "[e]";
          } else {
            currentFormStatus = "[p]";
          }
          let previousFormStatus = previousStatus;
          if (previousFormStatus === "completed") {
            previousFormStatus = "[a]";
          } else if (previousFormStatus === "editing") {
            previousFormStatus = "[e]";
          } else {
            previousFormStatus = "[p]";
          }
          const answerTextSpan = document.createElement("span");
          answerTextSpan.classList.add("has-tooltip");
          answerTextSpan.innerHTML = !isSubsequentCall
            ? `
                        ${previousVisitLabel}: ${label} (${formattedDate}) ${previousFormStatus}
                    `
            : "";

          if (!isSubsequentCall) {
            // Append the answerTextSpan to graderAnswersDiv
            graderAnswersDiv.appendChild(answerTextSpan);

            // Add the 'has-tooltip' class to the answerTextSpan
            answerTextSpan.classList.add("has-tooltip");

            // Create the tooltip span
            const tooltipSpan = document.createElement("span");
            tooltipSpan.classList.add("tooltip");

            // Append the tooltip span to the answerTextSpan
            answerTextSpan.appendChild(tooltipSpan);
          } else {
            // Find the first element with the class 'has-tooltip'
            const firstAnswerSpan =
              graderAnswersDiv.querySelector(".has-tooltip");

            if (firstAnswerSpan) {
              const tooltipSpan = firstAnswerSpan.querySelector(".tooltip");

              if (tooltipSpan) {
                // Safely append new content to the tooltip
                tooltipSpan.innerHTML += `${visitId}: ${label} (${visitDate}) ${currentFormStatus}<br>`;
              } else {
                console.error("Tooltip span not found inside has-tooltip span");
              }
            } else {
              // Optional: Create a new span if not found
              const newAnswerSpan = document.createElement("span");
              newAnswerSpan.classList.add("has-tooltip");
              graderAnswersDiv.appendChild(newAnswerSpan);

              const newTooltipSpan = document.createElement("span");
              newTooltipSpan.classList.add("tooltip");
              newAnswerSpan.appendChild(newTooltipSpan);
              // Now update the new tooltip span with the content
              newTooltipSpan.innerHTML += `${visitId}: ${label} (${visitDate}) ${currentFormStatus}<br>`;
            }
          }
        }
      }
    });
  }

  getLabelFromSchema(schema, questionKey, questionValue) {
    for (const component of schema?.components || []) {
      if (component?.components) {
        // If the component has nested questions, iterate through them
        for (const question of component.components) {
          const targetKey = question?.key;
          const checkboxes = [];

          if (
            question?.values &&
            question?.type === "radio" &&
            targetKey === questionKey
          ) {
            for (const item of question.values) {
              if (item?.value == questionValue) {
                return item?.label || "Label Not Found";
              }
            }
          } else if (
            question?.values &&
            question?.type === "selectboxes" &&
            targetKey === questionKey
          ) {
            for (let key in questionValue) {
              if (questionValue[key]) {
                if (!question.values[key - 1]) {
                  continue;
                }
                checkboxes.push(
                  question.values[key - 1]?.label || "Unknown Label",
                );
              }
            }
            return checkboxes;
          }
        }
      }
    }
    return questionValue;
  }

  printForm(event) {
    event.preventDefault();
    this.printFormModule.printForm();
  }

  // These methods are now handled by PrintFormModule defaults
  // Keeping them here only if needed for other purposes (not for print)

  // renderField is now handled by PrintFormModule defaults
  // Keeping it here only if needed for other purposes (not for print)

  getFormData(stackId) {
    const studyId = this.data.get("studyId");
    const url = `${document.location.origin}/studies/${studyId}/adjudications/get_adjudication_form_details`;
    return fetch(url, {
      headers: {
        "Content-Type": "application/json; charset=utf-8",
        "X-CSRF-Token": document.head
          .querySelector('meta[name="csrf-token"]')
          .getAttribute("content"),
      },
      method: "POST",
      body: JSON.stringify({
        stack_id: stackId,
      }),
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || "Failed to fetch grading form data");
        }
        return data;
      })
      .catch((error) => {
        console.error("Error fetching grading form details:", error);
        throw error;
      });
  }

  // These methods are now handled by PrintFormModule defaults
  // Keeping them here only if needed for other purposes (not for print)
}
